import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Package, ShoppingCart, Bot, Tag, ArrowLeft, Upload, X, Check, Download, Shield, Key, Newspaper, FolderOpen, XCircle, Image, Settings, Link2, Cloud, RefreshCw, AlertTriangle, Eye, EyeOff, ChevronUp, ChevronDown, Search, User, Send, MessageCircle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { adminLogin, adminVerify, admin2faAuth, admin2faSetup, admin2faVerify, admin2faDisable, adminChangePassword, getProducts, createProduct, updateProduct, deleteProduct, approveProduct, importProductsBackup, importCustomersBackup, getOrders, updateOrder, exportOrders, getPromos, createPromo, deletePromo, getPromoReferrals, getBotSettings, updateBotSettings, setupBot, uploadFile, getImageUrl, getSettings, updateSettings, getPaymentSettings, updatePaymentSettings, getEvents, createEvent, updateEvent, deleteEvent, approveEvent, rejectEvent, getCategories, createCategory, updateCategory, deleteCategory, reorderCategories, getCloudinarySettings, updateCloudinarySettings, getCloudinaryStats, cloudinaryCleanup, cloudinarySync, refreshExchangeRates, downloadBackup, sendBackupToTelegram, restoreBackup, startPhotoArchive, getPhotoArchiveStatus, getPhotoArchiveDownloadToken, adminListCustomers, adminCustomerDetail, adminSendCustomerMessage, adminDeleteCustomer, adminMessagesThreads, adminMessagesUnreadCount, adminCustomerThread, adminSendSiteMessage, getBackendUrl } from "@/lib/api";
import { toast } from "sonner";

function LoginForm({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needs2fa, setNeeds2fa] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [totpCode, setTotpCode] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin({ username, password });
      if (res.requires_2fa) {
        setNeeds2fa(true);
        setTempToken(res.temp_token);
        toast.info("Введите код из Google Authenticator");
      } else {
        localStorage.setItem("admin_token", res.token);
        onLogin(res);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Ошибка входа";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handle2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await admin2faAuth({ temp_token: tempToken, code: totpCode });
      localStorage.setItem("admin_token", res.token);
      onLogin(res);
    } catch {
      toast.error("Неверный код 2FA");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4" data-testid="admin-login">
      {needs2fa ? (
        <form onSubmit={handle2fa} className="w-full max-w-sm space-y-4 border border-zinc-800 p-6">
          <h1 className="font-display text-2xl tracking-wider text-center">2FA</h1>
          <p className="text-xs font-mono text-zinc-500 text-center">Введите код из Google Authenticator</p>
          <input value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))} maxLength={6} autoFocus
            className="w-full bg-zinc-900 border border-zinc-800 h-14 px-3 text-center text-xl text-white focus:outline-none focus:border-zinc-600 font-mono tracking-[0.3em]"
            placeholder="000000" data-testid="admin-2fa-code" />
          <button type="submit" disabled={loading || totpCode.length !== 6}
            className="w-full h-12 bg-white text-black font-display text-sm font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-50"
            data-testid="admin-2fa-submit">
            {loading ? "..." : "ПОДТВЕРДИТЬ"}
          </button>
        </form>
      ) : (
        <form onSubmit={handleLogin} className="w-full max-w-sm space-y-4 border border-zinc-800 p-6">
          <h1 className="font-display text-2xl tracking-wider text-center">ADMIN</h1>
          <p className="text-xs font-mono text-zinc-500 text-center">Amberglow</p>
          <input value={username} onChange={(e) => setUsername(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 h-12 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            placeholder="Логин" data-testid="admin-username" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 h-12 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            placeholder="Пароль" data-testid="admin-password" />
          <button type="submit" disabled={loading}
            className="w-full h-12 bg-white text-black font-display text-sm font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-50"
            data-testid="admin-login-btn">
            {loading ? "..." : "ВОЙТИ"}
          </button>
        </form>
      )}
    </div>
  );
}

function ProductForm({ product, onSave, onCancel, categories }) {
  const [form, setForm] = useState({
    title: product?.title || "", description: product?.description || "",
    price: product?.price || 0, old_price: product?.old_price || 0,
    sizes: product?.sizes?.join(", ") || "", category: product?.category || "other",
    brand: product?.brand || "", status: product?.status || "in-stock",
    condition: product?.condition || "", images: product?.images || [],
    show_in_new_arrivals: product?.show_in_new_arrivals || false,
    published: product?.published !== false,
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newUrlInput, setNewUrlInput] = useState("");

  const STATUSES = [
    { value: "in-stock", label: "В наличии" }, { value: "pre-order", label: "Под заказ" },
    { value: "sold-out", label: "Распродано" }, { value: "pending", label: "На модерации" },
  ];

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    
    const maxSize = 15 * 1024 * 1024;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/heic", "image/heif"];
    
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast.error(`${file.name}: слишком большой (макс. 15MB)`);
        return false;
      }
      // Check by extension if MIME type is empty or generic
      const ext = file.name.split('.').pop()?.toLowerCase();
      const validExts = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'heic', 'heif'];
      if (!allowedTypes.includes(file.type) && !validExts.includes(ext)) {
        toast.error(`${file.name}: неверный формат`);
        return false;
      }
      return true;
    });
    
    if (!validFiles.length) return;
    
    setUploading(true);
    let successCount = 0;
    
    for (const file of validFiles) {
      try {
        const res = await uploadFile(file);
        if (res.url) {
          setForm((f) => ({ ...f, images: [...f.images, res.url] }));
          successCount++;
        }
      } catch (err) {
        console.error("Upload error:", err);
        const errMsg = err?.response?.data?.detail || err?.message || "Неизвестная ошибка";
        toast.error(`Ошибка загрузки ${file.name}: ${errMsg}`);
      }
    }
    
    if (successCount > 0) {
      toast.success(`Загружено ${successCount} из ${validFiles.length} фото`);
    }
    setUploading(false);
    // Reset file input
    e.target.value = "";
  };

  const addUrlImage = () => {
    const url = newUrlInput.trim();
    if (!url) return;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      toast.error("URL должен начинаться с http:// или https://");
      return;
    }
    setForm((f) => ({ ...f, images: [...f.images, url] }));
    setNewUrlInput("");
    toast.success("Фото по ссылке добавлено");
  };

  const handleUrlKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addUrlImage();
    }
  };

  const handlePaste = (e) => {
    const pastedText = e.clipboardData.getData("text");
    // Check if pasted text contains multiple URLs (newline or comma separated)
    const urls = pastedText.split(/[\n,]+/).map(s => s.trim()).filter(s => s.startsWith("http://") || s.startsWith("https://"));
    if (urls.length > 1) {
      e.preventDefault();
      setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
      setNewUrlInput("");
      toast.success(`Добавлено ${urls.length} фото по ссылкам`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form, price: parseInt(form.price) || 0,
        old_price: parseInt(form.old_price) || null,
        sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (product) await updateProduct(product.id, data);
      else await createProduct(data);
      toast.success(product ? "Обновлено" : "Создано");
      onSave();
    } catch {
      toast.error("Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const moveImage = (idx, direction) => {
    setForm((f) => {
      const imgs = [...f.images];
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= imgs.length) return f;
      [imgs[idx], imgs[newIdx]] = [imgs[newIdx], imgs[idx]];
      return { ...f, images: imgs };
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Название</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            data-testid="product-title-input" />
        </div>
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Бренд</label>
          <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            data-testid="product-brand-input" />
        </div>
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Цена (RUB)</label>
          <input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
            data-testid="product-price-input" />
        </div>
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Старая цена (RUB)</label>
          <input type="number" value={form.old_price} onChange={(e) => setForm({ ...form, old_price: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
            data-testid="product-old-price-input" />
        </div>
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Размеры (через запятую)</label>
          <input value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            placeholder="S, M, L, XL" data-testid="product-sizes-input" />
        </div>
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Состояние</label>
          <input value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            placeholder="10/10" data-testid="product-condition-input" />
        </div>
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Категория</label>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            data-testid="product-category-select">
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label_ru}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Статус</label>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            data-testid="product-status-select">
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>
      {/* Show in New Arrivals checkbox */}
      <div className="flex items-center gap-3 py-2 px-1">
        <label className="relative inline-flex items-center cursor-pointer">
          <input 
            type="checkbox" 
            checked={form.show_in_new_arrivals} 
            onChange={(e) => setForm({ ...form, show_in_new_arrivals: e.target.checked })}
            className="sr-only peer"
            data-testid="product-new-arrivals-checkbox"
          />
          <div className="w-9 h-5 bg-zinc-800 border border-zinc-700 peer-checked:bg-green-600 peer-checked:border-green-500 rounded-sm transition-colors relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-zinc-400 after:peer-checked:bg-white after:w-3.5 after:h-3.5 after:rounded-sm after:transition-all peer-checked:after:translate-x-4"></div>
        </label>
        <div>
          <span className="text-sm text-white font-display">Показать в новых поступлениях</span>
          <p className="text-[10px] font-mono text-zinc-600 mt-0.5">Товар появится в ленте «Новые поступления» на главной</p>
        </div>
      </div>
      {/* Published on site toggle */}
      <div className="flex items-center gap-3 py-2 px-1">
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => setForm({ ...form, published: e.target.checked })}
            className="sr-only peer"
            data-testid="product-published-checkbox"
          />
          <div className="w-9 h-5 bg-zinc-800 border border-zinc-700 peer-checked:bg-green-600 peer-checked:border-green-500 rounded-sm transition-colors relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-zinc-400 after:peer-checked:bg-white after:w-3.5 after:h-3.5 after:rounded-sm after:transition-all peer-checked:after:translate-x-4"></div>
        </label>
        <div>
          <span className="text-sm text-white font-display">Опубликован на сайте</span>
          <p className="text-[10px] font-mono text-zinc-600 mt-0.5">Если выключено — товар скрыт от покупателей (виден только в админке)</p>
        </div>
      </div>
      <div>
        <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Описание</label>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full bg-zinc-900 border border-zinc-800 p-3 text-sm text-white focus:outline-none focus:border-zinc-600 min-h-[100px] resize-none"
          data-testid="product-description-input" />
      </div>
      <div>
        <label className="text-[11px] font-mono text-zinc-500 uppercase mb-1 block">
          ИЗОБРАЖЕНИЯ ({form.images.length})
        </label>
        <p className="text-[10px] font-mono text-zinc-600 mb-2">
          Рекомендуемый размер: <span className="text-zinc-400">1080×1350 px</span> (4:5, вертикальное). 
          Мин. ширина 800px. Любые пропорции будут корректно обрезаны.
        </p>
        
        {/* Image thumbnails grid */}
        <div className="flex flex-wrap gap-2 mb-3">
          {form.images.map((img, i) => (
            <div key={i} className="relative w-20 h-20 border border-zinc-800 overflow-hidden group bg-zinc-900 flex-shrink-0">
              <img src={getImageUrl(img, 200)} alt="" className="w-full h-full object-cover" 
                onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('flex', 'items-center', 'justify-center'); }}
              />
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {i > 0 && (
                  <button type="button" onClick={() => moveImage(i, -1)}
                    className="w-5 h-5 flex items-center justify-center text-zinc-300 hover:text-white text-xs">
                    ←
                  </button>
                )}
                <button type="button" onClick={() => removeImage(i)}
                  className="w-5 h-5 flex items-center justify-center text-red-400 hover:text-red-300">
                  <X className="w-3.5 h-3.5" />
                </button>
                {i < form.images.length - 1 && (
                  <button type="button" onClick={() => moveImage(i, 1)}
                    className="w-5 h-5 flex items-center justify-center text-zinc-300 hover:text-white text-xs">
                    →
                  </button>
                )}
              </div>
              {i === 0 && (
                <span className="absolute top-0 left-0 bg-white text-black text-[8px] font-mono px-1 leading-tight">
                  ГЛАВНОЕ
                </span>
              )}
            </div>
          ))}
          
          {/* Upload button */}
          <label className={`w-20 h-20 border border-dashed border-zinc-700 flex flex-col items-center justify-center cursor-pointer hover:border-zinc-500 transition-colors gap-0.5 flex-shrink-0 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
            {uploading ? (
              <span className="text-xs text-zinc-500 animate-pulse">...</span>
            ) : (
              <>
                <Upload className="w-4 h-4 text-zinc-500" />
                <span className="text-[8px] text-zinc-600 font-mono">ФАЙЛ</span>
              </>
            )}
            <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="product-image-upload" disabled={uploading} />
          </label>
        </div>

        {/* URL input section */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600" />
              <input 
                value={newUrlInput}
                onChange={(e) => setNewUrlInput(e.target.value)}
                onKeyDown={handleUrlKeyDown}
                onPaste={handlePaste}
                className="w-full bg-zinc-900 border border-zinc-800 h-10 pl-9 pr-3 text-xs text-white focus:outline-none focus:border-zinc-600 font-mono placeholder:text-zinc-700"
                placeholder="Вставьте URL фото (Enter для добавления)" 
                data-testid="product-image-url-input" 
              />
            </div>
            <button 
              type="button" 
              onClick={addUrlImage}
              disabled={!newUrlInput.trim()}
              className="h-10 px-4 bg-zinc-800 text-zinc-300 text-xs font-mono uppercase hover:bg-zinc-700 transition-colors disabled:opacity-30 disabled:pointer-events-none border border-zinc-700"
              data-testid="add-url-btn"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[9px] font-mono text-zinc-700">
            Можно вставить несколько URL через запятую или каждый на новой строке — все добавятся сразу. Файлы загружаются в облако (Cloudinary).
          </p>
        </div>

        {/* List of current image URLs */}
        {form.images.length > 0 && (
          <div className="mt-3 space-y-1 max-h-40 overflow-y-auto">
            {form.images.map((img, i) => (
              <div key={i} className="flex items-center gap-2 group">
                <span className="text-[9px] font-mono text-zinc-600 w-4 text-right flex-shrink-0">{i + 1}.</span>
                <span className="text-[9px] font-mono text-zinc-500 truncate flex-1" title={img}>
                  {img.length > 70 ? img.slice(0, 70) + "..." : img}
                </span>
                <button type="button" onClick={() => removeImage(i)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <X className="w-3 h-3 text-zinc-600 hover:text-red-400" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving}
          className="flex-1 h-11 bg-white text-black text-sm font-display font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-50"
          data-testid="product-save-btn">
          {saving ? "..." : product ? "ОБНОВИТЬ" : "СОЗДАТЬ"}
        </button>
        <button type="button" onClick={onCancel}
          className="h-11 px-6 border border-zinc-700 text-sm font-mono uppercase text-zinc-400 hover:bg-zinc-900 transition-colors"
          data-testid="product-cancel-btn">
          ОТМЕНА
        </button>
      </div>
    </form>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      getProducts({ limit: 500, include_pending: "true" }),
      getCategories()
    ]).then(([prodData, catData]) => {
      setProducts(prodData.products || []);
      setCategories(catData.categories || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить товар?")) return;
    try { await deleteProduct(id); toast.success("Удалено"); load(); } catch { toast.error("Ошибка"); }
  };

  const handleApprove = async (id) => {
    try { await approveProduct(id); toast.success("Товар опубликован!"); load(); } catch { toast.error("Ошибка"); }
  };

  const importRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const r = await importProductsBackup(file);
      toast.success(`Импортировано: ${r.imported}, пропущено (уже есть): ${r.skipped}. Товары скрыты — публикуйте по одному.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ошибка импорта");
    } finally { setImporting(false); }
  };

  const handleTogglePublished = async (p) => {
    try {
      await updateProduct(p.id, { published: p.published === false });
      toast.success(p.published === false ? "Опубликовано" : "Скрыто с сайта");
      load();
    } catch { toast.error("Ошибка"); }
  };

  if (showForm || editProduct) {
    return (
      <div>
        <h3 className="font-display text-base tracking-wider mb-4">{editProduct ? "РЕДАКТИРОВАТЬ ТОВАР" : "НОВЫЙ ТОВАР"}</h3>
        <ProductForm product={editProduct} categories={categories} onSave={() => { setShowForm(false); setEditProduct(null); load(); }}
          onCancel={() => { setShowForm(false); setEditProduct(null); }} />
      </div>
    );
  }

  // Filter products by search query and category
  const filteredProducts = products.filter((p) => {
    const matchesSearch = !searchQuery.trim() || p.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === "all" || p.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const pendingProducts = filteredProducts.filter(p => p.status === "pending");
  const activeProducts = filteredProducts.filter(p => p.status !== "pending");

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-mono text-zinc-500">{filteredProducts.length} из {products.length} товаров</span>
        <div className="flex items-center gap-2">
          <input ref={importRef} type="file" accept=".json,application/json" onChange={handleImport} className="hidden" data-testid="import-products-input" />
          <button onClick={() => importRef.current?.click()} disabled={importing}
            className="inline-flex items-center gap-2 px-4 h-10 border border-zinc-700 text-zinc-300 text-sm font-display font-bold uppercase hover:bg-zinc-800 transition-colors disabled:opacity-50"
            data-testid="import-products-btn" title="Импортировать товары из бэкапа (скрытыми)">
            <Upload className="w-4 h-4" /> {importing ? "..." : "ИМПОРТ"}
          </button>
          <button onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 h-10 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors"
            data-testid="add-product-btn">
            <Plus className="w-4 h-4" /> ДОБАВИТЬ
          </button>
        </div>
      </div>

      {/* Search + Category Filters */}
      <div className="mb-4 space-y-3">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 pl-10 pr-10 text-sm text-white focus:outline-none focus:border-zinc-600 placeholder:text-zinc-600"
            placeholder="Поиск по названию..."
            data-testid="admin-product-search"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-zinc-800 rounded transition-colors">
              <X className="w-4 h-4 text-zinc-500" />
            </button>
          )}
        </div>

        {/* Category filter chips */}
        <div className="flex flex-wrap gap-1.5" data-testid="admin-category-filters">
          <button
            onClick={() => setFilterCategory("all")}
            className={`px-3 py-1.5 text-[10px] font-mono uppercase border transition-colors ${
              filterCategory === "all"
                ? "bg-white text-black border-white"
                : "border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300"
            }`}
            data-testid="admin-filter-all"
          >
            ВСЕ
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => setFilterCategory(cat.slug)}
              className={`px-3 py-1.5 text-[10px] font-mono uppercase border transition-colors ${
                filterCategory === cat.slug
                  ? "bg-white text-black border-white"
                  : "border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`admin-filter-${cat.slug}`}
            >
              {cat.label_ru}
            </button>
          ))}
        </div>
      </div>

      {/* Pending products */}
      {pendingProducts.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-mono text-yellow-500 uppercase tracking-wider mb-3">НА МОДЕРАЦИИ ({pendingProducts.length})</p>
          <div className="space-y-2">
            {pendingProducts.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-3 border border-yellow-800/40 bg-yellow-950/10 hover:bg-yellow-950/20 transition-colors" data-testid={`admin-product-${p.id}`}>
                <div className="w-14 h-14 bg-zinc-950 shrink-0 overflow-hidden relative">
                  {p.images?.[0] && <img src={getImageUrl(p.images[0], 120)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-display truncate">{p.title}</p>
                  <p className="text-xs font-mono text-zinc-500 mt-0.5">{p.price} RUB / {p.category} / {p.images?.length || 0} фото</p>
                </div>
                <button onClick={() => handleApprove(p.id)} className="p-2 hover:bg-green-900/30 transition-colors" title="Опубликовать" data-testid={`approve-product-${p.id}`}>
                  <Check className="w-5 h-5 text-green-500" />
                </button>
                <button onClick={() => setEditProduct(p)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`edit-product-${p.id}`}>
                  <Pencil className="w-4 h-4 text-zinc-400" />
                </button>
                <button onClick={() => handleDelete(p.id)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`delete-product-${p.id}`}>
                  <Trash2 className="w-4 h-4 text-zinc-400" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? <div className="text-sm text-zinc-500 py-4">Загрузка...</div> : (
        <div className="space-y-2">
          {activeProducts.map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-3 border border-zinc-800/50 hover:bg-zinc-900/50 transition-colors" data-testid={`admin-product-${p.id}`}>
              <div className="w-14 h-14 bg-zinc-950 shrink-0 overflow-hidden relative">
                {p.images?.[0] && <img src={getImageUrl(p.images[0], 120)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-display truncate flex items-center gap-2">
                  {p.title}
                  {p.published === false && (
                    <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 bg-zinc-800 text-zinc-400 border border-zinc-700 shrink-0" data-testid={`product-hidden-badge-${p.id}`}>скрыт</span>
                  )}
                </p>
                <p className="text-xs font-mono text-zinc-500 mt-0.5">{p.price} RUB / {p.category} / {p.status}</p>
              </div>
              <button onClick={() => handleTogglePublished(p)}
                className="p-2 hover:bg-zinc-800 transition-colors"
                title={p.published === false ? "Опубликовать на сайте" : "Скрыть с сайта"}
                data-testid={`toggle-published-${p.id}`}>
                {p.published === false
                  ? <EyeOff className="w-4 h-4 text-zinc-500" />
                  : <Eye className="w-4 h-4 text-green-500" />}
              </button>
              <button onClick={() => setEditProduct(p)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`edit-product-${p.id}`}>
                <Pencil className="w-4 h-4 text-zinc-400" />
              </button>
              <button onClick={() => handleDelete(p.id)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`delete-product-${p.id}`}>
                <Trash2 className="w-4 h-4 text-zinc-400" />
              </button>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredProducts.length === 0 && (searchQuery || filterCategory !== "all") && (
        <div className="text-sm text-zinc-500 py-8 text-center">Ничего не найдено</div>
      )}
    </div>
  );
}

// ─── EVENTS TAB ───
function EventsTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getEvents({ include_pending: "true", limit: 500 }).then((d) => setEvents(d.events || []))
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    try { await approveEvent(id); toast.success("Событие опубликовано!"); load(); } catch { toast.error("Ошибка"); }
  };

  const handleReject = async (id) => {
    try { await rejectEvent(id); toast.success("Событие отклонено"); load(); } catch { toast.error("Ошибка"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить событие?")) return;
    try { await deleteEvent(id); toast.success("Удалено"); load(); } catch { toast.error("Ошибка"); }
  };

  if (showForm || editEvent) {
    return <EventForm event={editEvent} onSave={() => { setShowForm(false); setEditEvent(null); load(); }}
      onCancel={() => { setShowForm(false); setEditEvent(null); }} />;
  }

  const pendingEvents = events.filter(e => e.status === "pending");
  const approvedEvents = events.filter(e => e.status === "approved");
  const rejectedEvents = events.filter(e => e.status === "rejected");

  const statusColor = { pending: "text-yellow-500 border-yellow-800/40 bg-yellow-950/10", approved: "text-green-400 border-green-800/40 bg-green-950/10", rejected: "text-red-400 border-red-800/40 bg-red-950/10" };
  const statusLabel = { pending: "На модерации", approved: "Опубликовано", rejected: "Отклонено" };
  const typeLabels = { news: "Новость", article: "Статья", discount: "Скидка", arrival: "Поступление", info: "Инфо", new_arrival: "Новинка", price_change: "Цена", status_change: "Статус" };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-mono text-zinc-500">{events.length} событий</span>
        <button onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-4 h-10 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors"
          data-testid="add-event-btn">
          <Plus className="w-4 h-4" /> СОЗДАТЬ
        </button>
      </div>

      {/* Pending events */}
      {pendingEvents.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-mono text-yellow-500 uppercase tracking-wider mb-3">НА МОДЕРАЦИИ ({pendingEvents.length})</p>
          <div className="space-y-2">
            {pendingEvents.map((ev) => (
              <div key={ev.id} className="flex items-start gap-4 p-3 border border-yellow-800/40 bg-yellow-950/10 hover:bg-yellow-950/20 transition-colors" data-testid={`admin-event-${ev.id}`}>
                {ev.image && (
                  <div className="w-20 h-20 bg-zinc-950 shrink-0 overflow-hidden relative">
                    <img src={getImageUrl(ev.image, 200)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-yellow-500 uppercase border border-yellow-800/40 px-1.5 py-0.5">{typeLabels[ev.type] || ev.type}</span>
                  </div>
                  <p className="text-sm text-white font-display">{ev.title}</p>
                  {ev.description && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{ev.description}</p>}
                  <p className="text-[10px] font-mono text-zinc-600 mt-1">{new Date(ev.created_at).toLocaleString("ru-RU")}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => handleApprove(ev.id)} className="p-2 hover:bg-green-900/30 transition-colors" title="Опубликовать" data-testid={`approve-event-${ev.id}`}>
                    <Check className="w-5 h-5 text-green-500" />
                  </button>
                  <button onClick={() => handleReject(ev.id)} className="p-2 hover:bg-red-900/30 transition-colors" title="Отклонить" data-testid={`reject-event-${ev.id}`}>
                    <XCircle className="w-5 h-5 text-red-400" />
                  </button>
                  <button onClick={() => setEditEvent(ev)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`edit-event-${ev.id}`}>
                    <Pencil className="w-4 h-4 text-zinc-400" />
                  </button>
                  <button onClick={() => handleDelete(ev.id)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`delete-event-${ev.id}`}>
                    <Trash2 className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approved events */}
      {approvedEvents.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-mono text-green-400 uppercase tracking-wider mb-3">ОПУБЛИКОВАНО ({approvedEvents.length})</p>
          <div className="space-y-2">
            {approvedEvents.map((ev) => (
              <div key={ev.id} className="flex items-start gap-4 p-3 border border-zinc-800/50 hover:bg-zinc-900/50 transition-colors" data-testid={`admin-event-${ev.id}`}>
                {ev.image && (
                  <div className="w-20 h-20 bg-zinc-950 shrink-0 overflow-hidden relative">
                    <img src={getImageUrl(ev.image, 200)} alt="" className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-green-400 uppercase border border-green-800/40 px-1.5 py-0.5">{typeLabels[ev.type] || ev.type}</span>
                  </div>
                  <p className="text-sm text-white font-display">{ev.title}</p>
                  {ev.description && <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{ev.description}</p>}
                  <p className="text-[10px] font-mono text-zinc-600 mt-1">{new Date(ev.created_at).toLocaleString("ru-RU")}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditEvent(ev)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`edit-event-${ev.id}`}>
                    <Pencil className="w-4 h-4 text-zinc-400" />
                  </button>
                  <button onClick={() => handleDelete(ev.id)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`delete-event-${ev.id}`}>
                    <Trash2 className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rejected events */}
      {rejectedEvents.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-mono text-red-400 uppercase tracking-wider mb-3">ОТКЛОНЕНО ({rejectedEvents.length})</p>
          <div className="space-y-2">
            {rejectedEvents.map((ev) => (
              <div key={ev.id} className="flex items-start gap-4 p-3 border border-red-800/20 bg-red-950/5 hover:bg-red-950/10 transition-colors opacity-60" data-testid={`admin-event-${ev.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-display">{ev.title}</p>
                  <p className="text-[10px] font-mono text-zinc-600 mt-1">{new Date(ev.created_at).toLocaleString("ru-RU")}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => handleApprove(ev.id)} className="p-2 hover:bg-green-900/30 transition-colors" title="Восстановить">
                    <Check className="w-4 h-4 text-green-500" />
                  </button>
                  <button onClick={() => handleDelete(ev.id)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`delete-event-${ev.id}`}>
                    <Trash2 className="w-4 h-4 text-zinc-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && events.length === 0 && (
        <div className="text-sm text-zinc-500 py-8 text-center">Нет событий</div>
      )}
    </div>
  );
}

function EventForm({ event, onSave, onCancel }) {
  const [form, setForm] = useState({
    type: event?.type || "news",
    title: event?.title || "",
    description: event?.description || "",
    image: event?.image || "",
    product_id: event?.product_id || "",
    status: event?.status || "pending",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const EVENT_TYPES = [
    { value: "news", label: "Новость" },
    { value: "article", label: "Статья" },
    { value: "discount", label: "Скидка" },
    { value: "arrival", label: "Поступление" },
    { value: "info", label: "Информация о магазине" },
    { value: "new_arrival", label: "Новинка" },
    { value: "price_change", label: "Изменение цены" },
    { value: "status_change", label: "Изменение статуса" },
  ];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadFile(file);
      setForm((f) => ({ ...f, image: res.url }));
    } catch {
      toast.error("Ошибка загрузки");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Укажите заголовок"); return; }
    setSaving(true);
    try {
      if (event) {
        await updateEvent(event.id, form);
      } else {
        await createEvent(form);
      }
      toast.success(event ? "Обновлено" : "Создано");
      onSave();
    } catch {
      toast.error("Ошибка");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="event-form">
      <h3 className="font-display text-base tracking-wider">{event ? "РЕДАКТИРОВАТЬ СОБЫТИЕ" : "НОВОЕ СОБЫТИЕ"}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Тип</label>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            data-testid="event-type-select">
            {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Статус</label>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            data-testid="event-status-select">
            <option value="pending">На модерации</option>
            <option value="approved">Опубликовано</option>
            <option value="rejected">Отклонено</option>
          </select>
        </div>
      </div>

      <div>
        <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Заголовок</label>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
          placeholder="Заголовок события" data-testid="event-title-input" />
      </div>

      <div>
        <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Описание</label>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full bg-zinc-900 border border-zinc-800 p-3 text-sm text-white focus:outline-none focus:border-zinc-600 min-h-[120px] resize-none"
          placeholder="Подробное описание..." data-testid="event-description-input" />
      </div>

      <div>
        <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Изображение</label>
        <p className="text-[10px] font-mono text-zinc-600 mb-2">
          Рекомендуемый размер: <span className="text-zinc-400">1200×675 px</span> (16:9) для новостей, 
          <span className="text-zinc-400">1080×1350 px</span> (4:5) для товаров. 
          Макс. ширина 1920px. Любое фото будет корректно обрезано.
        </p>
        <div className="flex items-center gap-3">
          {form.image ? (
            <div className="relative w-24 h-24 border border-zinc-800 overflow-hidden group">
              <img src={getImageUrl(form.image, 240)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => setForm({ ...form, image: "" })}
                className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <label className={`w-24 h-24 border border-dashed border-zinc-700 flex flex-col items-center justify-center cursor-pointer hover:border-zinc-500 transition-colors gap-1 ${uploading ? "opacity-50" : ""}`}>
              <Image className="w-5 h-5 text-zinc-500" />
              <span className="text-[9px] font-mono text-zinc-600">ЗАГРУЗИТЬ</span>
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="event-image-upload" />
            </label>
          )}
          <div className="flex-1">
            <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-xs text-white focus:outline-none focus:border-zinc-600"
              placeholder="Или URL изображения" data-testid="event-image-url-input" />
          </div>
        </div>
      </div>

      <div>
        <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">ID товара (опционально)</label>
        <input value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}
          className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-xs text-white focus:outline-none focus:border-zinc-600 font-mono"
          placeholder="UUID товара для ссылки" data-testid="event-product-id-input" />
      </div>

      <div className="flex gap-3 pt-2">
        <button type="submit" disabled={saving}
          className="flex-1 h-11 bg-white text-black text-sm font-display font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-50"
          data-testid="event-save-btn">
          {saving ? "..." : event ? "ОБНОВИТЬ" : "СОЗДАТЬ"}
        </button>
        <button type="button" onClick={onCancel}
          className="h-11 px-6 border border-zinc-700 text-sm font-mono uppercase text-zinc-400 hover:bg-zinc-900 transition-colors"
          data-testid="event-cancel-btn">
          ОТМЕНА
        </button>
      </div>
    </form>
  );
}

// ─── CATEGORIES TAB ───
function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState({ slug: "", label_ru: "", label_en: "" });

  const load = useCallback(() => {
    setLoading(true);
    getCategories().then((d) => setCategories(d.categories || []))
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ slug: "", label_ru: "", label_en: "" });
    setShowForm(false);
    setEditCat(null);
  };

  const handleEdit = (cat) => {
    setEditCat(cat);
    setForm({ slug: cat.slug, label_ru: cat.label_ru, label_en: cat.label_en });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.slug.trim() || !form.label_ru.trim()) {
      toast.error("Заполните slug и название (RU)");
      return;
    }
    try {
      if (editCat) {
        await updateCategory(editCat.id, form);
        toast.success("Категория обновлена");
      } else {
        await createCategory(form);
        toast.success("Категория создана");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить категорию?")) return;
    try { await deleteCategory(id); toast.success("Удалено"); load(); } catch { toast.error("Ошибка"); }
  };

  const handleMove = async (index, direction) => {
    const newCats = [...categories];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newCats.length) return;
    [newCats[index], newCats[swapIndex]] = [newCats[swapIndex], newCats[index]];
    setCategories(newCats);
    try {
      await reorderCategories(newCats.map(c => c.id));
    } catch { toast.error("Ошибка сортировки"); load(); }
  };

  return (
    <div className="space-y-6" data-testid="categories-tab">
      <div className="flex items-center justify-between">
        <span className="text-sm font-mono text-zinc-500">{categories.length} категорий</span>
        {!showForm && (
          <button onClick={() => { setShowForm(true); setEditCat(null); setForm({ slug: "", label_ru: "", label_en: "" }); }}
            className="inline-flex items-center gap-2 px-4 h-10 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors"
            data-testid="add-category-btn">
            <Plus className="w-4 h-4" /> ДОБАВИТЬ
          </button>
        )}
      </div>

      {showForm && (
        <div className="border border-zinc-800 p-4 space-y-4">
          <h3 className="font-display text-base tracking-wider">{editCat ? "РЕДАКТИРОВАТЬ" : "НОВАЯ КАТЕГОРИЯ"}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Slug (EN)</label>
              <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
                placeholder="my-category" data-testid="category-slug-input" />
            </div>
            <div>
              <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Название (RU)</label>
              <input value={form.label_ru} onChange={(e) => setForm({ ...form, label_ru: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
                placeholder="Моя категория" data-testid="category-label-ru-input" />
            </div>
            <div>
              <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Название (EN)</label>
              <input value={form.label_en} onChange={(e) => setForm({ ...form, label_en: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
                placeholder="My Category" data-testid="category-label-en-input" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={handleSave}
              className="flex-1 h-11 bg-white text-black text-sm font-display font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors"
              data-testid="category-save-btn">
              {editCat ? "ОБНОВИТЬ" : "СОЗДАТЬ"}
            </button>
            <button onClick={resetForm}
              className="h-11 px-6 border border-zinc-700 text-sm font-mono uppercase text-zinc-400 hover:bg-zinc-900 transition-colors"
              data-testid="category-cancel-btn">
              ОТМЕНА
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="text-sm text-zinc-500 py-4">Загрузка...</div> : (
        <div className="space-y-2">
          {categories.map((cat, index) => (
            <div key={cat.id} className="flex items-center gap-2 p-3 border border-zinc-800/50 hover:bg-zinc-900/50 transition-colors" data-testid={`admin-category-${cat.id}`}>
              <div className="flex flex-col">
                <button onClick={() => handleMove(index, -1)} disabled={index === 0}
                  className="p-1 hover:bg-zinc-800 transition-colors disabled:opacity-20">
                  <ChevronUp className="w-3 h-3 text-zinc-400" />
                </button>
                <button onClick={() => handleMove(index, 1)} disabled={index === categories.length - 1}
                  className="p-1 hover:bg-zinc-800 transition-colors disabled:opacity-20">
                  <ChevronDown className="w-3 h-3 text-zinc-400" />
                </button>
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-4">
                <span className="text-xs font-mono text-zinc-600 w-24 shrink-0">{cat.slug}</span>
                <span className="text-sm text-white">{cat.label_ru}</span>
                <span className="text-sm text-zinc-500">{cat.label_en}</span>
              </div>
              <button onClick={() => handleEdit(cat)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`edit-category-${cat.id}`}>
                <Pencil className="w-4 h-4 text-zinc-400" />
              </button>
              <button onClick={() => handleDelete(cat.id)} className="p-2 hover:bg-zinc-800 transition-colors" data-testid={`delete-category-${cat.id}`}>
                <Trash2 className="w-4 h-4 text-zinc-400" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getOrders().then((d) => setOrders(d.orders || []))
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatus = async (id, status) => {
    try { await updateOrder(id, status); toast.success("Обновлено"); load(); } catch { toast.error("Ошибка"); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const blob = await exportOrders(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_${dateFrom || "all"}_${dateTo || "all"}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Файл скачан");
    } catch {
      toast.error("Ошибка экспорта");
    } finally {
      setExporting(false);
    }
  };

  const statusColors = { new: "bg-blue-500", confirmed: "bg-green-500", shipped: "bg-yellow-500", completed: "bg-zinc-500", cancelled: "bg-red-500" };
  const statusLabels = { new: "Новый", confirmed: "Подтвержден", shipped: "Отправлен", completed: "Завершен", cancelled: "Отменен" };

  return (
    <div>
      {/* Export controls */}
      <div className="flex flex-wrap items-end gap-3 mb-4 pb-4 border-b border-zinc-800">
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">От</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 h-10 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
            data-testid="export-date-from" />
        </div>
        <div>
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">До</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 h-10 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
            data-testid="export-date-to" />
        </div>
        <button onClick={handleExport} disabled={exporting}
          className="inline-flex items-center gap-2 h-10 px-4 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors disabled:opacity-50"
          data-testid="export-excel-btn">
          <Download className="w-4 h-4" />
          {exporting ? "..." : "EXCEL"}
        </button>
        <span className="text-xs font-mono text-zinc-600 ml-auto">{orders.length} заказов</span>
      </div>

      {loading ? <div className="text-sm text-zinc-500 py-4">Загрузка...</div> : orders.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Нет заказов</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} className="border border-zinc-800 p-4 space-y-3" data-testid={`order-${o.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono text-zinc-400">#{o.id.slice(0, 8)}</span>
                  <div className={`w-2.5 h-2.5 ${statusColors[o.status] || "bg-zinc-500"}`} />
                  <span className="text-sm text-zinc-400">{statusLabels[o.status] || o.status}</span>
                </div>
                <span className="text-sm font-mono text-zinc-600">{new Date(o.created_at).toLocaleDateString("ru-RU")}</span>
              </div>
              <div className="text-sm text-zinc-300">
                {o.items?.map((item, i) => (
                  <p key={i}>{item.title} x{item.quantity} - {item.price * item.quantity} RUB {item.size && `(${item.size})`}</p>
                ))}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-sm font-mono font-bold">{o.total} RUB</p>
                  <p className="text-xs text-zinc-500">{o.customer_name} / {o.customer_contact}</p>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {["confirmed", "shipped", "completed", "cancelled"].map((s) => (
                    <button key={s} onClick={() => handleStatus(o.id, s)}
                      className={`px-3 py-1.5 text-[10px] font-mono uppercase border transition-colors ${o.status === s ? "bg-white text-black border-white" : "border-zinc-700 text-zinc-500 hover:border-zinc-500"}`}
                      data-testid={`order-status-${s}-${o.id}`}>
                      {statusLabels[s]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BotTab() {
  const [settings, setSettings] = useState(null);
  const [newId, setNewId] = useState("");
  const [newPin, setNewPin] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getBotSettings().then(setSettings).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const addWhitelist = async () => {
    const id = parseInt(newId);
    if (!id) return;
    const wl = [...(settings?.whitelist || []), id];
    try { await updateBotSettings({ whitelist: wl }); setNewId(""); load(); toast.success("ID добавлен"); } catch { toast.error("Ошибка"); }
  };

  const removeWhitelist = async (id) => {
    const wl = (settings?.whitelist || []).filter((i) => i !== id);
    try { await updateBotSettings({ whitelist: wl }); load(); } catch { toast.error("Ошибка"); }
  };

  const changePin = async () => {
    if (newPin.length !== 4) { toast.error("PIN должен быть 4 цифры"); return; }
    try { await updateBotSettings({ pin: newPin }); setNewPin(""); load(); toast.success("PIN обновлен"); } catch { toast.error("Ошибка"); }
  };

  const handleSetup = async () => {
    try { const r = await setupBot(); toast.success("Webhook: " + (r.ok ? "OK" : "Error")); } catch { toast.error("Ошибка"); }
  };

  if (loading) return <div className="text-sm text-zinc-500 py-4">Загрузка...</div>;

  return (
    <div className="space-y-8" data-testid="bot-settings">
      <div>
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-3">WHITELIST (Telegram ID)</h3>
        <div className="space-y-2 mb-3">
          {(settings?.whitelist || []).map((id) => (
            <div key={id} className="flex items-center justify-between px-4 py-3 border border-zinc-800/50">
              <span className="text-sm font-mono">{id}</span>
              <button onClick={() => removeWhitelist(id)} className="text-sm text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <input value={newId} onChange={(e) => setNewId(e.target.value)}
            className="flex-1 bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
            placeholder="Telegram User ID" data-testid="whitelist-input" />
          <button onClick={addWhitelist}
            className="px-5 h-11 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors"
            data-testid="whitelist-add-btn">
            ДОБАВИТЬ
          </button>
        </div>
        <p className="text-xs text-zinc-600 mt-2">Отправьте /start боту чтобы узнать свой Telegram ID</p>
      </div>
      <div>
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-3">PIN-КОД</h3>
        <p className="text-sm text-zinc-400 mb-3">Текущий PIN: <span className="font-mono">{settings?.pin || "1234"}</span></p>
        <div className="flex gap-3">
          <input value={newPin} onChange={(e) => setNewPin(e.target.value)} maxLength={4}
            className="w-36 bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono text-center tracking-widest"
            placeholder="0000" data-testid="pin-input" />
          <button onClick={changePin}
            className="px-5 h-11 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors"
            data-testid="pin-change-btn">
            ИЗМЕНИТЬ
          </button>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-3">ADMIN CHAT ID</h3>
        <p className="text-sm text-zinc-400">Текущий: <span className="font-mono">{settings?.admin_chat_id || "Не установлен"}</span></p>
        <p className="text-xs text-zinc-600 mt-2">Устанавливается автоматически при первой авторизации в боте</p>
      </div>
      <button onClick={handleSetup}
        className="h-11 px-5 border border-zinc-700 text-sm font-mono uppercase text-zinc-400 hover:bg-zinc-900 transition-colors"
        data-testid="bot-setup-btn">
        ПЕРЕУСТАНОВИТЬ WEBHOOK
      </button>
    </div>
  );
}

function CustomersTab({ onOpenChat }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("last_seen");
  const [selected, setSelected] = useState(null);  // {customer, orders}
  const [selLoading, setSelLoading] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await adminListCustomers({ q: q || undefined, sort });
      setCustomers(r.items || []);
    } catch { toast.error("Не удалось загрузить клиентов"); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [sort]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = async (c) => {
    setSelected({ customer: c, orders: [] });
    setSelLoading(true);
    setMsgText("");
    try {
      const d = await adminCustomerDetail(c.id);
      setSelected({ customer: d.customer, orders: d.orders || [] });
    } catch { toast.error("Не удалось загрузить детали"); }
    finally { setSelLoading(false); }
  };

  const sendMsg = async () => {
    if (!msgText.trim()) return;
    setMsgSending(true);
    try {
      await adminSendCustomerMessage(selected.customer.id, msgText);
      toast.success("Сообщение отправлено клиенту в Telegram");
      setMsgText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка отправки");
    } finally { setMsgSending(false); }
  };

  const handleDeleteCustomer = async (c, e) => {
    e?.stopPropagation?.();
    const name = [c.tg_first_name, c.tg_last_name].filter(Boolean).join(" ") || c.tg_username || c.username || "клиента";
    const ok = window.confirm(
      `Удалить ${name}?\n\nЭто действие нельзя отменить. Будут удалены:\n• профиль клиента\n• вся переписка\n• все заказы клиента`
    );
    if (!ok) return;
    try {
      const r = await adminDeleteCustomer(c.id);
      toast.success(`Клиент удалён (${r.deleted_messages || 0} сообщ., ${r.deleted_orders || 0} заказ.)`);
      if (selected?.customer?.id === c.id) setSelected(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось удалить");
    }
  };

  const formatRub = (n) => `${(n || 0).toLocaleString("ru-RU")} ₽`;
  const formatDate = (s) => s ? new Date(s).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" }) : "—";

  const importRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const handleImportCustomers = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const r = await importCustomersBackup(file);
      toast.success(`Клиенты импортированы: ${r.imported}, пропущено (уже есть): ${r.skipped}. Суммы покупок сброшены в 0.`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ошибка импорта");
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="customers-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="font-display text-lg tracking-wider flex-1">КЛИЕНТЫ ({customers.length})</h2>
        <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-3 h-10 flex-1 max-w-xs">
          <Search className="w-4 h-4 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder="username, имя..." className="flex-1 bg-transparent text-sm text-white focus:outline-none placeholder:text-zinc-600"
            data-testid="customers-search-input" />
        </div>
        <button onClick={reload} className="h-10 px-3 bg-zinc-800 hover:bg-zinc-700 text-xs font-mono uppercase" data-testid="customers-search-btn">Найти</button>
        <input ref={importRef} type="file" accept=".json,application/json" onChange={handleImportCustomers} className="hidden" data-testid="import-customers-input" />
        <button onClick={() => importRef.current?.click()} disabled={importing}
          className="h-10 px-3 inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 text-xs font-mono uppercase hover:bg-zinc-800 transition-colors disabled:opacity-50"
          data-testid="import-customers-btn" title="Импортировать клиентов из бэкапа (суммы покупок = 0)">
          <Upload className="w-4 h-4" /> {importing ? "..." : "ИМПОРТ"}
        </button>
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="h-10 px-3 bg-zinc-900 border border-zinc-800 text-xs font-mono text-zinc-300 focus:outline-none"
          data-testid="customers-sort">
          <option value="last_seen">По активности</option>
          <option value="orders">По заказам</option>
          <option value="spent">По сумме</option>
          <option value="created">По дате регистрации</option>
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 py-8 text-center">Загрузка...</div>
      ) : customers.length === 0 ? (
        <div className="text-sm text-zinc-500 py-8 text-center" data-testid="customers-empty">
          Клиентов пока нет. Они появятся после подтверждения через Telegram при оформлении заказа.
        </div>
      ) : (
        <div className="space-y-1">
          {customers.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-zinc-800/50 hover:bg-zinc-900/60 transition-colors group" data-testid={`customer-row-${c.id}`}>
              <button onClick={() => openDetail(c)}
                className="flex-1 min-w-0 flex items-center gap-3 p-3 text-left"
                data-testid={`customer-open-${c.id}`}>
                <div className="w-10 h-10 rounded-full bg-zinc-800 shrink-0 flex items-center justify-center text-white font-display font-bold text-sm">
                  {(c.tg_first_name || c.tg_username || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <p className="text-sm text-white font-display truncate">
                      {[c.tg_first_name, c.tg_last_name].filter(Boolean).join(" ") || "Без имени"}
                    </p>
                    {c.tg_username && <span className="text-xs font-mono text-zinc-500 shrink-0">@{c.tg_username}</span>}
                  </div>
                  <p className="text-[11px] font-mono text-zinc-500 mt-0.5">
                    {c.orders_count || 0} заказов • {formatRub(c.total_spent)} • {formatDate(c.last_seen_at)}
                  </p>
                </div>
                {c.orders_count >= 3 && <span className="text-amber-400 text-base shrink-0">⭐</span>}
              </button>
              {onOpenChat && (
                <button onClick={(e) => { e.stopPropagation(); onOpenChat(c.id); }}
                  className="p-3 text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors shrink-0"
                  title="Написать сообщение на сайте"
                  data-testid={`customer-chat-${c.id}`}>
                  <MessageCircle className="w-4 h-4" />
                </button>
              )}
              <button onClick={(e) => handleDeleteCustomer(c, e)}
                className="p-3 text-zinc-500 hover:text-red-400 hover:bg-red-950/30 transition-colors shrink-0"
                title="Удалить клиента"
                data-testid={`customer-delete-${c.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-detail-modal">
            <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 p-4 flex items-center justify-between">
              <h3 className="font-display text-base tracking-wider">КАРТОЧКА КЛИЕНТА</h3>
              <div className="flex items-center gap-1">
                {onOpenChat && (
                  <button onClick={() => { onOpenChat(selected.customer.id); setSelected(null); }}
                    className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
                    title="Открыть переписку на сайте"
                    data-testid="customer-detail-open-chat">
                    <MessageCircle className="w-4 h-4" />
                  </button>
                )}
                <button onClick={(e) => handleDeleteCustomer(selected.customer, e)}
                  className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-950/30 transition-colors"
                  title="Удалить клиента"
                  data-testid="customer-detail-delete">
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={() => setSelected(null)} className="p-1 hover:bg-zinc-800" data-testid="customer-detail-close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center text-white font-display font-bold text-xl shrink-0">
                  {(selected.customer.tg_first_name || selected.customer.tg_username || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-display">{[selected.customer.tg_first_name, selected.customer.tg_last_name].filter(Boolean).join(" ") || "Без имени"}</p>
                  {selected.customer.tg_username && (
                    <p className="text-xs font-mono text-zinc-400">@{selected.customer.tg_username}</p>
                  )}
                  <p className="text-[10px] font-mono text-zinc-600 mt-1">chat_id: {selected.customer.tg_chat_id}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border border-zinc-800 p-2">
                  <p className="text-[10px] font-mono text-zinc-500 uppercase">Заказов</p>
                  <p className="text-lg font-display font-bold mt-0.5">{selected.customer.orders_count || 0}</p>
                </div>
                <div className="border border-zinc-800 p-2">
                  <p className="text-[10px] font-mono text-zinc-500 uppercase">Сумма</p>
                  <p className="text-lg font-display font-bold mt-0.5">{formatRub(selected.customer.total_spent)}</p>
                </div>
                <div className="border border-zinc-800 p-2">
                  <p className="text-[10px] font-mono text-zinc-500 uppercase">С нами</p>
                  <p className="text-xs font-mono mt-1">{formatDate(selected.customer.verified_at)}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <MessageCircle className="w-3.5 h-3.5" /> Отправить в Telegram
                </p>
                <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)}
                  placeholder="Привет! Ваш заказ готов к оплате..."
                  className="w-full bg-zinc-900 border border-zinc-800 p-3 text-sm text-white focus:outline-none focus:border-zinc-600 min-h-[80px] resize-none"
                  data-testid="customer-msg-input" />
                <button onClick={sendMsg} disabled={msgSending || !msgText.trim()}
                  className="mt-2 h-10 px-4 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white text-xs font-display font-bold uppercase tracking-wider flex items-center gap-2"
                  data-testid="customer-msg-send-btn">
                  <Send className="w-3.5 h-3.5" />
                  {msgSending ? "Отправка..." : "Отправить"}
                </button>
              </div>

              <div>
                <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-2">История заказов ({selected.orders.length})</p>
                {selLoading ? (
                  <div className="text-xs text-zinc-500">Загрузка...</div>
                ) : selected.orders.length === 0 ? (
                  <div className="text-xs text-zinc-500">Пока нет заказов</div>
                ) : (
                  <div className="space-y-1">
                    {selected.orders.map((o) => (
                      <div key={o.id} className="flex items-center justify-between p-2 border border-zinc-800/60 text-xs" data-testid={`customer-order-${o.id}`}>
                        <div>
                          <p className="font-mono">#{o.id.slice(0, 8)}</p>
                          <p className="text-[10px] text-zinc-500">{formatDate(o.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold">{formatRub(o.total)}</p>
                          <p className="text-[10px] text-zinc-500 uppercase">{o.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MessagesTab({ initialCustomerId, onConsumed }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);  // {customer_id, customer:{}, messages:[]}
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerBusy, setPickerBusy] = useState(false);
  const scrollRef = useRef(null);

  const reloadThreads = async () => {
    try {
      const r = await adminMessagesThreads();
      setThreads(r.items || []);
    } catch { toast.error("Не удалось загрузить переписки"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    reloadThreads();
    const id = setInterval(reloadThreads, 15000);
    return () => clearInterval(id);
  }, []);

  const openThreadById = async (customerId) => {
    setSelected({ customer_id: customerId, customer: {}, messages: [] });
    try {
      const d = await adminCustomerThread(customerId);
      const c = d.customer || {};
      const display = c.display_name
        || [c.tg_first_name, c.tg_last_name].filter(Boolean).join(" ")
        || c.tg_username
        || c.username
        || "Клиент";
      setSelected({ customer_id: customerId, customer: { ...c, display_name: display }, messages: d.messages || [] });
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
      reloadThreads();
    } catch { toast.error("Ошибка загрузки переписки"); }
  };

  // Honour initialCustomerId passed from CustomersTab → "open chat" button.
  useEffect(() => {
    if (initialCustomerId) {
      openThreadById(initialCustomerId);
      onConsumed && onConsumed();
    }
  }, [initialCustomerId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openThread = async (t) => {
    setSelected({ customer_id: t.customer_id, customer: t, messages: [] });
    try {
      const d = await adminCustomerThread(t.customer_id);
      setSelected({ customer_id: t.customer_id, customer: { ...t, ...d.customer }, messages: d.messages || [] });
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
      // Refresh thread list to update unread counters
      reloadThreads();
    } catch { toast.error("Ошибка загрузки переписки"); }
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !selected) return;
    setSending(true);
    try {
      const m = await adminSendSiteMessage(selected.customer_id, text);
      // Dedup: poll may have already pulled the new message between request and response.
      setSelected((p) => {
        if (!p) return p;
        if (p.messages.some((x) => x.id === m.id)) return p;
        return { ...p, messages: [...p.messages, m] };
      });
      setDraft("");
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка");
    } finally { setSending(false); }
  };

  // Poll the open thread every 5 s for new customer messages, preserving any local-only ones.
  useEffect(() => {
    if (!selected) return;
    const id = setInterval(async () => {
      try {
        const d = await adminCustomerThread(selected.customer_id);
        const incoming = d.messages || [];
        const serverIds = new Set(incoming.map((m) => m.id));
        setSelected((p) => {
          if (!p || p.customer_id !== selected.customer_id) return p;
          const localExtras = p.messages.filter((m) => !serverIds.has(m.id));
          return { ...p, messages: [...incoming, ...localExtras] };
        });
      } catch (_) { /* silent retry next tick */ }
    }, 5000);
    return () => clearInterval(id);
  }, [selected?.customer_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (iso) => {
    try { return new Date(iso).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" }); }
    catch { return ""; }
  };

  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  // Search customers when picker is open
  useEffect(() => {
    if (!pickerOpen) return;
    setPickerBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await adminListCustomers({ q: pickerQ || undefined, sort: "last_seen", limit: 30 });
        setPickerResults(r.items || []);
      } catch { /* silent */ }
      finally { setPickerBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [pickerOpen, pickerQ]);

  const pickCustomer = (c) => {
    setPickerOpen(false);
    setPickerQ("");
    openThreadById(c.id);
  };

  return (
    <div data-testid="messages-tab">
      <div className="flex items-center justify-between mb-4 gap-3">
        <h2 className="font-display text-lg tracking-wider">
          СООБЩЕНИЯ {totalUnread > 0 && <span className="ml-2 text-xs font-mono text-emerald-400">• {totalUnread} непрочитано</span>}
        </h2>
        <button onClick={() => setPickerOpen(true)}
          className="px-3 py-2 bg-white text-black font-mono text-[11px] uppercase tracking-wider hover:bg-zinc-200 transition-colors flex items-center gap-2"
          data-testid="messages-new-chat-btn">
          <Send className="w-3.5 h-3.5" /> Написать клиенту
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[60vh]">
        {/* Thread list */}
        <div className="lg:col-span-1 border border-zinc-800 max-h-[70vh] overflow-y-auto" data-testid="messages-thread-list">
          {loading ? (
            <div className="text-sm text-zinc-500 p-4 text-center">Загрузка...</div>
          ) : threads.length === 0 ? (
            <div className="text-sm text-zinc-500 p-4 text-center" data-testid="messages-empty">
              Переписок пока нет
            </div>
          ) : threads.map((t) => (
            <button key={t.customer_id} onClick={() => openThread(t)}
              className={`w-full text-left p-3 border-b border-zinc-800/60 hover:bg-zinc-900/60 transition-colors ${selected?.customer_id === t.customer_id ? "bg-zinc-900" : ""}`}
              data-testid={`thread-${t.customer_id}`}>
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <p className="text-sm font-display truncate flex items-center gap-1.5">
                  {t.unread > 0 && <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />}
                  {t.display_name}
                </p>
                <span className="text-[10px] font-mono text-zinc-600 shrink-0">{formatTime(t.last_at)}</span>
              </div>
              <p className="text-[11px] font-mono text-zinc-500 truncate">
                {t.last_from === "admin" ? "Вы: " : ""}{t.last_text}
              </p>
              {t.unread > 0 && (
                <span className="text-[10px] font-mono text-emerald-400">{t.unread} новое</span>
              )}
            </button>
          ))}
        </div>

        {/* Conversation pane */}
        <div className="lg:col-span-2 border border-zinc-800 flex flex-col max-h-[70vh]">
          {selected ? (
            <>
              <div className="border-b border-zinc-800 p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-display" data-testid="messages-conversation-name">{selected.customer?.display_name || "—"}</p>
                  <p className="text-[10px] font-mono text-zinc-500">
                    {selected.customer?.username ? `@${selected.customer.username}` : ""}
                    {selected.customer?.tg_username ? ` • Telegram: @${selected.customer.tg_username}` : ""}
                  </p>
                </div>
                <button onClick={() => setSelected(null)} className="p-1 text-zinc-500 hover:text-white" data-testid="messages-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4">
                {selected.messages.length === 0 ? (
                  <div className="text-center text-zinc-500 text-xs py-10">Начните беседу</div>
                ) : selected.messages.map((m, idx) => {
                  const prev = selected.messages[idx - 1];
                  const newSender = !prev || prev.from !== m.from;
                  return (
                    <div key={m.id} className={`flex ${m.from === "admin" ? "justify-end" : "justify-start"} ${newSender ? "mt-8 first:mt-0" : "mt-4"}`} data-testid={`admin-msg-${m.id}`}>
                      <div className={`max-w-[80%] px-4 py-3 ${m.from === "admin" ? "bg-white text-black" : "bg-zinc-900 text-white border border-zinc-800"}`}>
                        <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{m.text}</p>
                        <p className={`text-[10px] font-mono mt-2 ${m.from === "admin" ? "text-zinc-500" : "text-zinc-600"}`}>{formatTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="border-t border-zinc-800 p-3 flex gap-2">
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder="Ответ клиенту..." rows={2}
                  className="flex-1 bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-white focus:outline-none focus:border-zinc-600 resize-none"
                  data-testid="messages-reply-input" />
                <button onClick={handleSend} disabled={sending || !draft.trim()}
                  className="px-4 bg-white text-black hover:bg-zinc-200 disabled:opacity-40 transition-colors" data-testid="messages-reply-send">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center px-6">
              <div className="space-y-1">
                <MessageCircle className="w-10 h-10 mx-auto text-zinc-700" strokeWidth={1.5} />
                <p className="text-sm text-zinc-400">Выберите переписку слева</p>
                <p className="text-[11px] font-mono text-zinc-600">Зелёная точка — есть непрочитанные</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Customer picker — write to any customer */}
      {pickerOpen && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setPickerOpen(false)}>
          <div className="bg-zinc-950 border border-zinc-800 w-full max-w-lg mt-12" onClick={(e) => e.stopPropagation()} data-testid="customer-picker-modal">
            <div className="border-b border-zinc-800 p-4 flex items-center justify-between">
              <h3 className="font-display text-base tracking-wider">ВЫБРАТЬ КЛИЕНТА</h3>
              <button onClick={() => setPickerOpen(false)} className="p-1 hover:bg-zinc-800" data-testid="customer-picker-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input autoFocus value={pickerQ} onChange={(e) => setPickerQ(e.target.value)}
                  placeholder="Поиск по имени, нику или логину..."
                  className="w-full bg-zinc-900 border border-zinc-800 pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-zinc-600"
                  data-testid="customer-picker-search" />
              </div>
              <div className="max-h-[55vh] overflow-y-auto border border-zinc-800/50">
                {pickerBusy ? (
                  <div className="text-sm text-zinc-500 p-4 text-center">Поиск...</div>
                ) : pickerResults.length === 0 ? (
                  <div className="text-sm text-zinc-500 p-6 text-center">
                    {pickerQ ? "Ничего не найдено" : "Нет клиентов"}
                  </div>
                ) : pickerResults.map((c) => {
                  const display = [c.tg_first_name, c.tg_last_name].filter(Boolean).join(" ")
                    || c.display_name || c.tg_username || c.username || "Без имени";
                  const sub = c.tg_username ? `@${c.tg_username}` : (c.username ? `login: ${c.username}` : "");
                  return (
                    <button key={c.id} onClick={() => pickCustomer(c)}
                      className="w-full text-left flex items-center gap-3 p-3 border-b border-zinc-800/40 hover:bg-zinc-900/60 transition-colors"
                      data-testid={`picker-customer-${c.id}`}>
                      <div className="w-9 h-9 rounded-full bg-zinc-800 shrink-0 flex items-center justify-center text-white font-display font-bold text-xs">
                        {display.slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-display truncate">{display}</p>
                        {sub && <p className="text-[10px] font-mono text-zinc-500 truncate">{sub}</p>}
                      </div>
                      {c.tg_chat_id ? (
                        <span className="text-[9px] font-mono text-emerald-400 shrink-0">TG</span>
                      ) : (
                        <span className="text-[9px] font-mono text-zinc-500 shrink-0">САЙТ</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState("");
  const [amount, setAmount] = useState("");
  const [isReferral, setIsReferral] = useState(false);
  const [expandedReferral, setExpandedReferral] = useState(null);
  const [referralData, setReferralData] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    getPromos().then((d) => setPromos(d.promos || []))
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!code.trim()) return;
    try {
      await createPromo({
        code,
        discount_percent: parseInt(percent) || 0,
        discount_amount: parseInt(amount) || 0,
        is_referral: isReferral,
      });
      setCode(""); setPercent(""); setAmount(""); setIsReferral(false);
      load(); toast.success("Промокод создан");
    } catch { toast.error("Ошибка"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Удалить промокод?")) return;
    try { await deletePromo(id); load(); toast.success("Удалено"); } catch { toast.error("Ошибка"); }
  };

  const toggleReferralDetails = async (promoId) => {
    if (expandedReferral === promoId) {
      setExpandedReferral(null);
      return;
    }
    setExpandedReferral(promoId);
    if (!referralData[promoId]) {
      try {
        const data = await getPromoReferrals(promoId);
        setReferralData((prev) => ({ ...prev, [promoId]: data }));
      } catch { toast.error("Ошибка загрузки рефералов"); }
    }
  };

  return (
    <div className="space-y-4" data-testid="promos-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <input value={code} onChange={(e) => setCode(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono uppercase"
          placeholder="КОД" data-testid="promo-code-input" />
        <input value={percent} onChange={(e) => setPercent(e.target.value)} type="number"
          className="bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
          placeholder="% скидка" data-testid="promo-percent-input" />
        <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number"
          className="bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
          placeholder="RUB скидка" data-testid="promo-amount-input" />
        <button onClick={handleAdd}
          className="h-11 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors"
          data-testid="promo-add-btn">
          СОЗДАТЬ
        </button>
      </div>

      {/* Referral checkbox */}
      <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="promo-referral-toggle">
        <input type="checkbox" checked={isReferral} onChange={(e) => setIsReferral(e.target.checked)}
          className="w-4 h-4 accent-white bg-zinc-900 border-zinc-700" />
        <span className="text-xs font-mono text-zinc-400">РЕФЕРАЛЬНЫЙ ПРОМОКОД</span>
      </label>

      {loading ? <div className="text-sm text-zinc-500 py-4">Загрузка...</div> : (
        <div className="space-y-2">
          {promos.map((p) => (
            <div key={p.id} className="border border-zinc-800/50" data-testid={`promo-${p.id}`}>
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-sm font-mono font-bold">{p.code}</span>
                  <span className="text-sm text-zinc-500">
                    {p.discount_percent ? `${p.discount_percent}%` : `${p.discount_amount} RUB`}
                  </span>
                  {p.is_referral && (
                    <button
                      onClick={() => toggleReferralDetails(p.id)}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono uppercase bg-blue-950/50 border border-blue-800/50 text-blue-400 hover:bg-blue-900/40 transition-colors"
                      data-testid={`referral-badge-${p.id}`}
                    >
                      РЕФ · {p.referral_count || 0} чел.
                    </button>
                  )}
                  {p.uses_left >= 0 && (
                    <span className="text-[10px] font-mono text-zinc-600">осталось: {p.uses_left}</span>
                  )}
                </div>
                <button onClick={() => handleDelete(p.id)} className="text-sm text-zinc-500 hover:text-red-400 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Expanded referral details */}
              {p.is_referral && expandedReferral === p.id && (
                <div className="px-4 pb-3 border-t border-zinc-800/30">
                  <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider pt-2 mb-2">
                    Подтверждённые рефералы ({referralData[p.id]?.count || 0})
                  </p>
                  {referralData[p.id]?.referrals?.length > 0 ? (
                    <div className="space-y-1">
                      {referralData[p.id].referrals.map((r) => (
                        <div key={r.id} className="flex items-center justify-between text-xs font-mono py-1 px-2 bg-zinc-900/50">
                          <span className="text-zinc-300">{r.customer_name || "—"}</span>
                          <span className="text-zinc-500">{r.customer_contact || "—"}</span>
                          <span className="text-zinc-600">{r.total} RUB</span>
                          <span className="text-zinc-700">{(r.created_at || "").slice(0, 10)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-600 font-mono">Нет подтверждённых рефералов</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SecurityTab() {
  const [has2fa, setHas2fa] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminVerify().then((d) => setHas2fa(d.has_2fa || false))
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSetup2fa = async () => {
    try {
      const data = await admin2faSetup();
      setQrData(data);
      toast.info("Отсканируйте QR-код в Google Authenticator");
    } catch { toast.error("Ошибка"); }
  };

  const handleVerify2fa = async () => {
    if (verifyCode.length !== 6) return;
    try {
      await admin2faVerify(verifyCode);
      setHas2fa(true);
      setQrData(null);
      setVerifyCode("");
      toast.success("2FA включена!");
    } catch { toast.error("Неверный код"); }
  };

  const handleDisable2fa = async () => {
    if (disableCode.length !== 6) return;
    try {
      await admin2faDisable(disableCode);
      setHas2fa(false);
      setDisableCode("");
      toast.success("2FA отключена");
    } catch { toast.error("Неверный код"); }
  };

  const handleChangePassword = async () => {
    if (newPw.length < 8) { toast.error("Минимум 8 символов"); return; }
    try {
      await adminChangePassword({ old_password: oldPw, new_password: newPw });
      setOldPw(""); setNewPw("");
      toast.success("Пароль изменен");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка");
    }
  };

  if (loading) return <div className="text-sm text-zinc-500 py-4">Загрузка...</div>;

  return (
    <div className="space-y-8" data-testid="security-tab">
      <div>
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4" /> ДВУХФАКТОРНАЯ АУТЕНТИФИКАЦИЯ (2FA)
        </h3>
        {has2fa ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <Check className="w-4 h-4" /> 2FA включена
            </div>
            <p className="text-sm text-zinc-500">Для отключения введите код из Google Authenticator:</p>
            <div className="flex gap-3">
              <input value={disableCode} onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
                className="w-44 bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono text-center tracking-widest"
                placeholder="000000" data-testid="2fa-disable-code" />
              <button onClick={handleDisable2fa}
                className="px-5 h-11 border border-red-800 text-red-400 text-sm font-mono uppercase hover:bg-red-900/20 transition-colors"
                data-testid="2fa-disable-btn">
                ОТКЛЮЧИТЬ
              </button>
            </div>
          </div>
        ) : qrData ? (
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">1. Отсканируйте QR-код в Google Authenticator</p>
            <div className="w-52 h-52 border border-zinc-800 overflow-hidden">
              <img src={qrData.qr_code} alt="QR" className="w-full h-full" />
            </div>
            <p className="text-xs font-mono text-zinc-600 break-all">Ключ: {qrData.secret}</p>
            <p className="text-sm text-zinc-400">2. Введите 6-значный код для подтверждения:</p>
            <div className="flex gap-3">
              <input value={verifyCode} onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
                className="w-44 bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono text-center tracking-widest"
                placeholder="000000" data-testid="2fa-verify-code" />
              <button onClick={handleVerify2fa}
                className="px-5 h-11 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors"
                data-testid="2fa-verify-btn">
                ПОДТВЕРДИТЬ
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-zinc-400 mb-3">2FA не включена. Настоятельно рекомендуем включить для защиты аккаунта.</p>
            <button onClick={handleSetup2fa}
              className="inline-flex items-center gap-2 h-11 px-5 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors"
              data-testid="2fa-setup-btn">
              <Shield className="w-4 h-4" /> ВКЛЮЧИТЬ 2FA
            </button>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Key className="w-4 h-4" /> СМЕНА ПАРОЛЯ
        </h3>
        <div className="space-y-3 max-w-md">
          <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            placeholder="Текущий пароль" data-testid="pw-old" />
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            placeholder="Новый пароль (мин. 8 символов)" data-testid="pw-new" />
          <button onClick={handleChangePassword}
            className="h-11 px-5 bg-white text-black text-sm font-display font-bold uppercase hover:bg-zinc-200 transition-colors"
            data-testid="pw-change-btn">
            ИЗМЕНИТЬ ПАРОЛЬ
          </button>
        </div>
      </div>

      <div className="border border-zinc-800 p-5">
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-3">ЗАМЕТКИ ПО БЕЗОПАСНОСТИ</h3>
        <ul className="text-sm text-zinc-400 space-y-2">
          <li>URL админки: <span className="font-mono text-zinc-300">/sz-manage-84</span> (скрыт от публичного меню)</li>
          <li>Rate-limiting: 5 попыток входа, блокировка IP на 5 минут</li>
          <li>Пароль: минимум 8 символов, запрещены простые пароли</li>
          <li>2FA: Google Authenticator (TOTP) — рекомендуем включить</li>
        </ul>
      </div>
    </div>
  );
}

function StoreSettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshingRates, setRefreshingRates] = useState(false);
  const [payForm, setPayForm] = useState({ provider: "bepaid", enabled: false, mode: "test", shop_id: "", secret_key: "", public_key: "", service_no: "", secret_key_set: false, public_key_set: false });
  const [savingPay, setSavingPay] = useState(false);
  const [form, setForm] = useState({
    store_name: "", store_description_ru: "", store_description_en: "",
    exchange_rates: { USD: 95, EUR: 103, BYN: 27 },
    telegram_contact: "XruuXruu",
    exchange_rate_update_interval_hours: 3,
    company_legal_name: "", company_unp: "", company_address: "", company_bank: "", company_reg_info: "",
    trade_register_number: "", trade_register_date: "", belgie_info: "",
    contact_email: "", contact_phone: "", work_hours: "",
    legal_offer: "", legal_return: "", legal_delivery: "", legal_privacy: "", legal_cookie: "",
  });

  // Cloudinary state
  const [clSettings, setClSettings] = useState(null);
  const [clStats, setClStats] = useState(null);
  const [clForm, setClForm] = useState({
    cloud_name: "", api_key: "", api_secret: "",
    cleanup_minutes: 10, auto_cleanup_enabled: false,
  });
  const [clSaving, setClSaving] = useState(false);
  const [clCleaning, setClCleaning] = useState(false);
  const [clSyncing, setClSyncing] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [downloadingPhotos, setDownloadingPhotos] = useState(false);
  const [photosProgress, setPhotosProgress] = useState(null); // {downloaded, total, succeeded, failed, status}

  // Telegram backup state
  const [tgBackup, setTgBackup] = useState({
    telegram_backup_enabled: false,
    telegram_backup_interval_hours: 24,
    telegram_backup_last_sent: null,
    telegram_backup_last_status: null,
    admin_chat_id: null,
  });
  const [savingTgBackup, setSavingTgBackup] = useState(false);
  const [sendingTgBackup, setSendingTgBackup] = useState(false);

  // Restore state
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreCode, setRestoreCode] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  const reloadBotSettings = async () => {
    try {
      const bs = await getBotSettings();
      setTgBackup({
        telegram_backup_enabled: !!bs.telegram_backup_enabled,
        telegram_backup_interval_hours: bs.telegram_backup_interval_hours || 24,
        telegram_backup_last_sent: bs.telegram_backup_last_sent || null,
        telegram_backup_last_status: bs.telegram_backup_last_status || null,
        admin_chat_id: bs.admin_chat_id || null,
      });
    } catch (e) { console.error(e); }
  };

  const handleSaveTgBackup = async () => {
    setSavingTgBackup(true);
    try {
      await updateBotSettings({
        telegram_backup_enabled: tgBackup.telegram_backup_enabled,
        telegram_backup_interval_hours: Math.max(1, parseInt(tgBackup.telegram_backup_interval_hours) || 24),
      });
      toast.success("Настройки авто-бэкапа сохранены");
      await reloadBotSettings();
    } catch { toast.error("Ошибка сохранения"); }
    finally { setSavingTgBackup(false); }
  };

  const handleSendTgBackupNow = async () => {
    if (!window.confirm("Отправить полный бэкап базы в Telegram (в admin-чат) прямо сейчас?")) return;
    setSendingTgBackup(true);
    try {
      const r = await sendBackupToTelegram();
      toast.success(`Бэкап отправлен: ${r.filename} (${Math.round((r.size_bytes || 0) / 1024)} KB)`);
      await reloadBotSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка отправки");
    } finally { setSendingTgBackup(false); }
  };

  const handleRestore = async () => {
    if (!restoreFile) return toast.error("Выберите файл бэкапа (.json или .zip)");
    if (!restoreCode || restoreCode.length < 6) return toast.error("Введите 6-значный код 2FA");
    const firstConfirm = window.confirm(
      "ВНИМАНИЕ! Восстановление ПОЛНОСТЬЮ заменит содержимое всех коллекций текущей базы данными из бэкапа.\n\n" +
      "Эту операцию НЕЛЬЗЯ отменить. Продолжить?"
    );
    if (!firstConfirm) return;
    const doubleCheck = window.prompt('Для подтверждения введите слово ВОССТАНОВИТЬ');
    if ((doubleCheck || "").trim().toUpperCase() !== "ВОССТАНОВИТЬ") {
      return toast.error("Отменено");
    }
    setRestoring(true);
    setRestoreResult(null);
    try {
      const r = await restoreBackup(restoreFile, restoreCode);
      setRestoreResult(r);
      toast.success(`Восстановлено: ${r.total_docs} документов в ${Object.keys(r.restored_collections || {}).length} коллекциях`);
      setRestoreCode("");
      setRestoreFile(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Ошибка восстановления";
      toast.error(msg);
    } finally { setRestoring(false); }
  };

  const handleDownloadPhotos = async () => {
    if (!window.confirm(
      "Скачать архив всех фото товаров и событий?\n\n" +
      "Сервер скачает каждое фото с Cloudinary (потребит трафик, но даст полную страховку), " +
      "упакует в ZIP с manifest.json. Процесс может занять 2–5 минут — окно можно не закрывать."
    )) return;
    setDownloadingPhotos(true);
    setPhotosProgress({ status: "starting", downloaded: 0, total: 0, succeeded: 0, failed: 0 });
    try {
      const { job_id } = await startPhotoArchive();
      // Poll status every 2s
      let job;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2000));
        job = await getPhotoArchiveStatus(job_id);
        setPhotosProgress({
          status: job.status,
          downloaded: job.downloaded || 0,
          total: job.total || 0,
          succeeded: job.succeeded || 0,
          failed: job.failed || 0,
        });
        if (job.status === "ready" || job.status === "error") break;
      }
      if (job.status === "error") {
        toast.error(`Ошибка: ${job.error || "unknown"}`);
        return;
      }
      // Native browser download via signed token (bypasses JS Blob memory limits & axios timeouts)
      const { token } = await getPhotoArchiveDownloadToken(job_id);
      const backendBase = getBackendUrl();
      const downloadUrl = `${backendBase}/api/admin/photos/archive/download/${job_id}?t=${encodeURIComponent(token)}`;
      // Use a hidden anchor click — browser handles streaming, progress, and saving natively
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.rel = "noopener";
      // download attribute helps some browsers use the suggested filename
      a.download = `sizzhka_photos_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Архив готов: ${job.succeeded}/${job.total} фото. Скачивание началось в браузере.`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Ошибка при скачивании архива фото");
    } finally {
      setDownloadingPhotos(false);
    }
  };

  const handleDownloadBackup = async () => {
    if (!window.confirm("Скачать полный бэкап базы данных? Файл будет содержать ВСЕ данные магазина (товары, заказы, настройки и т.д.).")) return;
    setDownloadingBackup(true);
    try {
      const response = await downloadBackup();
      // Extract filename from Content-Disposition header, fallback to generated name
      let filename = `sizzhka_backup_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
      const cd = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];
      if (cd) {
        const m = cd.match(/filename="?([^";]+)"?/);
        if (m) filename = m[1];
      }
      const blob = new Blob([response.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Бэкап успешно скачан");
    } catch (err) {
      console.error(err);
      toast.error("Ошибка при скачивании бэкапа");
    } finally {
      setDownloadingBackup(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getSettings(),
      getCloudinarySettings().catch(() => null),
      getCloudinaryStats().catch(() => null),
      getPaymentSettings().catch(() => null),
    ]).then(([s, cl, stats, pay]) => {
      setSettings(s);
      setForm({
        store_name: s.store_name || "",
        store_description_ru: s.store_description_ru || "",
        store_description_en: s.store_description_en || "",
        exchange_rates: s.exchange_rates || { USD: 95, EUR: 103, BYN: 27 },
        telegram_contact: s.telegram_contact || "",
        exchange_rate_update_interval_hours: s.exchange_rate_update_interval_hours || 3,
        company_legal_name: s.company_legal_name || "",
        company_unp: s.company_unp || "",
        company_address: s.company_address || "",
        company_bank: s.company_bank || "",
        company_reg_info: s.company_reg_info || "",
        trade_register_number: s.trade_register_number || "",
        trade_register_date: s.trade_register_date || "",
        belgie_info: s.belgie_info || "",
        contact_email: s.contact_email || "",
        contact_phone: s.contact_phone || "",
        work_hours: s.work_hours || "",
        legal_offer: s.legal_offer || "",
        legal_return: s.legal_return || "",
        legal_delivery: s.legal_delivery || "",
        legal_privacy: s.legal_privacy || "",
        legal_cookie: s.legal_cookie || "",
      });
      if (cl) {
        setClSettings(cl);
        setClForm({
          cloud_name: cl.cloud_name || "",
          api_key: cl.api_key || "",
          api_secret: "", // Don't fill secret - it's masked
          cleanup_minutes: cl.cleanup_minutes ?? 10,
          auto_cleanup_enabled: cl.auto_cleanup_enabled ?? false,
        });
      }
      if (stats) setClStats(stats);
      if (pay) setPayForm({
        provider: pay.provider || "bepaid",
        enabled: !!pay.enabled,
        mode: pay.mode || "test",
        shop_id: pay.shop_id || "",
        secret_key: "",
        public_key: "",
        service_no: pay.service_no || "",
        secret_key_set: !!pay.secret_key_set,
        public_key_set: !!pay.public_key_set,
      });
    }).catch(console.error).finally(() => setLoading(false));
    reloadBotSettings();
  }, []);

  const handleSavePay = async () => {
    setSavingPay(true);
    try {
      const data = { ...payForm };
      delete data.secret_key_set; delete data.public_key_set;
      if (!data.secret_key) delete data.secret_key;
      if (!data.public_key) delete data.public_key;
      await updatePaymentSettings(data);
      toast.success("Настройки оплаты сохранены");
    } catch { toast.error("Ошибка"); }
    finally { setSavingPay(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings(form);
      toast.success("Настройки сохранены");
    } catch { toast.error("Ошибка"); }
    finally { setSaving(false); }
  };

  const handleClSave = async () => {
    setClSaving(true);
    try {
      const data = { ...clForm };
      // Don't send empty secret (keep existing)
      if (!data.api_secret) delete data.api_secret;
      await updateCloudinarySettings(data);
      // Refresh settings and stats
      const [cl, stats] = await Promise.all([
        getCloudinarySettings().catch(() => null),
        getCloudinaryStats().catch(() => null),
      ]);
      if (cl) setClSettings(cl);
      if (stats) setClStats(stats);
      toast.success("Cloudinary настройки сохранены");
    } catch (err) {
      toast.error("Ошибка сохранения Cloudinary");
    } finally { setClSaving(false); }
  };

  const handleCleanup = async () => {
    setClCleaning(true);
    try {
      const result = await cloudinaryCleanup();
      toast.success(result.message || `Удалено ${result.deleted} фото`);
      const stats = await getCloudinaryStats().catch(() => null);
      if (stats) setClStats(stats);
    } catch { toast.error("Ошибка очистки"); }
    finally { setClCleaning(false); }
  };

  const handleSync = async () => {
    setClSyncing(true);
    try {
      const result = await cloudinarySync();
      toast.success(`Синхронизировано: ${result.marked_in_use} используемых из ${result.total_tracked}`);
      const stats = await getCloudinaryStats().catch(() => null);
      if (stats) setClStats(stats);
    } catch { toast.error("Ошибка синхронизации"); }
    finally { setClSyncing(false); }
  };

  if (loading) return <div className="text-sm text-zinc-500 py-4">Загрузка...</div>;

  return (
    <div className="space-y-8" data-testid="store-settings-tab">
      {/* Store Settings */}
      <div>
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-4">ОСНОВНЫЕ НАСТРОЙКИ</h3>
        <div className="space-y-4 max-w-xl">
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Название магазина</label>
            <input value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
              placeholder="MY STORE" data-testid="settings-store-name" />
          </div>
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Описание (RU)</label>
            <textarea value={form.store_description_ru} onChange={(e) => setForm({ ...form, store_description_ru: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 p-3 text-sm text-white focus:outline-none focus:border-zinc-600 min-h-[80px] resize-none"
              placeholder="Описание на русском..." data-testid="settings-desc-ru" />
          </div>
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Описание (EN)</label>
            <textarea value={form.store_description_en} onChange={(e) => setForm({ ...form, store_description_en: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 p-3 text-sm text-white focus:outline-none focus:border-zinc-600 min-h-[80px] resize-none"
              placeholder="Description in English..." data-testid="settings-desc-en" />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-4">КУРСЫ ВАЛЮТ (1 ед. = X RUB)</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-xl">
          {Object.entries(form.exchange_rates).map(([curr, rate]) => (
            <div key={curr}>
              <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">{curr}</label>
              <input type="number" step="0.01" value={rate}
                onChange={(e) => setForm({ ...form, exchange_rates: { ...form.exchange_rates, [curr]: parseFloat(e.target.value) || 0 } })}
                className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
                data-testid={`settings-rate-${curr.toLowerCase()}`} />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-3 max-w-xl">
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Период обновления (часы)</label>
            <input type="number" min="1" max="168" value={form.exchange_rate_update_interval_hours}
              onChange={(e) => setForm({ ...form, exchange_rate_update_interval_hours: parseInt(e.target.value) || 3 })}
              className="w-28 bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
              data-testid="settings-update-interval" />
          </div>
          <button onClick={async () => {
            setRefreshingRates(true);
            try {
              const result = await refreshExchangeRates();
              if (result.exchange_rates) {
                setForm({ ...form, exchange_rates: result.exchange_rates });
                setSettings({ ...settings, exchange_rates_last_updated: result.last_updated });
                toast.success("Курсы обновлены");
              }
            } catch { toast.error("Ошибка обновления курсов"); }
            finally { setRefreshingRates(false); }
          }}
            disabled={refreshingRates}
            className="h-11 px-4 border border-zinc-700 text-sm font-mono uppercase text-zinc-300 hover:bg-zinc-800 transition-colors disabled:opacity-50 flex items-center gap-2 mt-5"
            data-testid="refresh-rates-btn">
            <RefreshCw className={`w-4 h-4 ${refreshingRates ? "animate-spin" : ""}`} />
            {refreshingRates ? "..." : "ОБНОВИТЬ СЕЙЧАС"}
          </button>
        </div>
        {settings?.exchange_rates_last_updated && (
          <p className="text-xs text-zinc-600 mt-2">Последнее обновление: {new Date(settings.exchange_rates_last_updated).toLocaleString("ru-RU")}</p>
        )}
        <p className="text-xs text-zinc-600 mt-1">Курсы обновляются автоматически с Open Exchange Rates API</p>
      </div>

      {/* Telegram Contact */}
      <div>
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-4">КОНТАКТ ДЛЯ ВОПРОСОВ</h3>
        <div className="max-w-xl">
          <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Telegram username (без @)</label>
          <input value={form.telegram_contact || ""} onChange={(e) => setForm({ ...form, telegram_contact: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
            placeholder="XruuXruu" data-testid="settings-telegram-contact" />
          <p className="text-xs text-zinc-600 mt-1">Кнопка &quot;Задать вопрос&quot; на карточке товара будет вести на https://t.me/{form.telegram_contact || "..."}</p>
        </div>
      </div>

      {/* Legal requisites (ИП / УНП / Trade Register / contacts) */}
      <div data-testid="settings-legal-section">
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-4">РЕКВИЗИТЫ И ЮР. ИНФОРМАЦИЯ</h3>
        <p className="text-xs text-zinc-600 mb-4 max-w-xl">Эти данные показываются в футере сайта и на юридических страницах. Обязательны для интернет-магазина в Беларуси. Пустые поля не отображаются.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          {[
            ["company_legal_name", "Продавец (ИП / ФИО)"],
            ["company_unp", "УНП"],
            ["company_address", "Адрес"],
            ["company_bank", "Расчётный счёт (банк, Р/с)"],
            ["company_reg_info", "Госрегистрация (свидетельство)"],
            ["trade_register_number", "№ в Торговом реестре"],
            ["trade_register_date", "Дата внесения в Торговый реестр"],
            ["belgie_info", "Регистрация в БелГИЭ"],
            ["work_hours", "Режим работы"],
            ["contact_email", "Email"],
            ["contact_phone", "Телефон"],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">{label}</label>
              <input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600"
                data-testid={`settings-${key.replace(/_/g, "-")}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Legal document texts */}
      <div data-testid="settings-legal-texts">
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-4">ЮРИДИЧЕСКИЕ ДОКУМЕНТЫ (ТЕКСТЫ)</h3>
        <p className="text-xs text-zinc-600 mb-4 max-w-xl">Строка, начинающаяся с «## », отображается как подзаголовок. Абзацы разделяйте пустой строкой.</p>
        <div className="space-y-4 max-w-3xl">
          {[
            ["legal_offer", "Публичная оферта"],
            ["legal_return", "Возврат и обмен"],
            ["legal_delivery", "Доставка и оплата"],
            ["legal_privacy", "Обработка персональных данных"],
            ["legal_cookie", "Файлы cookie"],
          ].map(([key, label]) => (
            <div key={key}>
              <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">{label}</label>
              <textarea value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 p-3 text-sm text-white focus:outline-none focus:border-zinc-600 min-h-[160px] resize-y font-mono text-xs leading-relaxed"
                data-testid={`settings-${key.replace(/_/g, "-")}`} />
            </div>
          ))}
        </div>
      </div>

      {/* Payment (bePaid) */}
      <div data-testid="settings-payment-section">
        <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider mb-4">ОНЛАЙН-ОПЛАТА (bePaid)</h3>
        <p className="text-xs text-zinc-600 mb-4 max-w-xl">Заготовка под приём оплаты картой и через ЕРИП. Пока выключено — заказы подтверждаются через Telegram/менеджера, никаких списаний не происходит. Заполните данные из личного кабинета bePaid и включите, когда всё будет готово.</p>
        <div className="flex items-center gap-3 py-2 px-1 mb-2">
          <label className="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" checked={payForm.enabled} onChange={(e) => setPayForm({ ...payForm, enabled: e.target.checked })} className="sr-only peer" data-testid="payment-enabled-checkbox" />
            <div className="w-9 h-5 bg-zinc-800 border border-zinc-700 peer-checked:bg-green-600 peer-checked:border-green-500 rounded-sm transition-colors relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-zinc-400 after:peer-checked:bg-white after:w-3.5 after:h-3.5 after:rounded-sm after:transition-all peer-checked:after:translate-x-4"></div>
          </label>
          <span className="text-sm text-white font-display">Онлайн-оплата включена</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Режим</label>
            <select value={payForm.mode} onChange={(e) => setPayForm({ ...payForm, mode: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600" data-testid="payment-mode">
              <option value="test">Тест (test)</option>
              <option value="live">Боевой (live)</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Shop ID</label>
            <input value={payForm.shop_id} onChange={(e) => setPayForm({ ...payForm, shop_id: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600" data-testid="payment-shop-id" />
          </div>
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Secret Key {payForm.secret_key_set && <span className="text-green-500">(сохранён)</span>}</label>
            <input type="password" value={payForm.secret_key} onChange={(e) => setPayForm({ ...payForm, secret_key: e.target.value })}
              placeholder={payForm.secret_key_set ? "•••••• (не менять — оставьте пустым)" : ""}
              className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600" data-testid="payment-secret-key" />
          </div>
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Public Key (для webhook) {payForm.public_key_set && <span className="text-green-500">(сохранён)</span>}</label>
            <input type="password" value={payForm.public_key} onChange={(e) => setPayForm({ ...payForm, public_key: e.target.value })}
              placeholder={payForm.public_key_set ? "•••••• (не менять — оставьте пустым)" : ""}
              className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600" data-testid="payment-public-key" />
          </div>
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">ЕРИП service_no</label>
            <input value={payForm.service_no} onChange={(e) => setPayForm({ ...payForm, service_no: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600" data-testid="payment-service-no" />
          </div>
        </div>
        <button onClick={handleSavePay} disabled={savingPay}
          className="mt-4 h-11 px-6 border border-zinc-700 text-zinc-200 text-sm font-display font-bold uppercase tracking-wider hover:bg-zinc-800 transition-colors disabled:opacity-50"
          data-testid="payment-save-btn">
          {savingPay ? "..." : "СОХРАНИТЬ ОПЛАТУ"}
        </button>
      </div>

      <button onClick={handleSave} disabled={saving}
        className="h-11 px-8 bg-white text-black text-sm font-display font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-50"
        data-testid="settings-save-btn">
        {saving ? "..." : "СОХРАНИТЬ"}
      </button>

      {/* Cloudinary Section */}
      <div className="border-t border-zinc-800 pt-8">
        <div className="flex items-center gap-2 mb-4">
          <Cloud className="w-5 h-5 text-blue-400" />
          <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider">ФОТО-ХОСТИНГ (CLOUDINARY)</h3>
        </div>

        {/* Connection Status */}
        <div className="mb-4">
          {clSettings?.cloud_name ? (
            <div className="flex items-center gap-2 text-xs font-mono">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-green-400">Подключено</span>
              <span className="text-zinc-600">•</span>
              <span className="text-zinc-500">{clSettings.cloud_name}</span>
              {clSettings.source === "env" && <span className="text-zinc-700">(из .env)</span>}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs font-mono">
              <div className="w-2 h-2 rounded-full bg-red-500"></div>
              <span className="text-red-400">Не подключено</span>
            </div>
          )}
        </div>

        {/* Cloudinary Credentials */}
        <div className="space-y-4 max-w-xl mb-6">
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Cloud Name</label>
            <input value={clForm.cloud_name} onChange={(e) => setClForm({ ...clForm, cloud_name: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
              placeholder="dxxxxxxxxx" data-testid="cl-cloud-name" />
          </div>
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">API Key</label>
            <input value={clForm.api_key} onChange={(e) => setClForm({ ...clForm, api_key: e.target.value })}
              className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
              placeholder="000000000000000" data-testid="cl-api-key" />
          </div>
          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">
              API Secret
              {clSettings?.api_secret_masked && (
                <span className="text-zinc-700 normal-case ml-2">текущий: {clSettings.api_secret_masked}</span>
              )}
            </label>
            <div className="relative">
              <input 
                type={showSecret ? "text" : "password"}
                value={clForm.api_secret} 
                onChange={(e) => setClForm({ ...clForm, api_secret: e.target.value })}
                className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 pr-10 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
                placeholder="Оставьте пустым чтобы не менять" data-testid="cl-api-secret" />
              <button type="button" onClick={() => setShowSecret(!showSecret)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-600 hover:text-zinc-400">
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Cleanup Settings */}
        <div className="space-y-4 max-w-xl mb-6">
          <h4 className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider">АВТООЧИСТКА НЕИСПОЛЬЗУЕМЫХ ФОТО</h4>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Удалять через (мин)</label>
              <input type="number" min="1" max="1440" value={clForm.cleanup_minutes}
                onChange={(e) => setClForm({ ...clForm, cleanup_minutes: parseInt(e.target.value) || 10 })}
                className="w-full bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
                data-testid="cl-cleanup-minutes" />
            </div>
            <div className="flex-1 pt-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <div className={`w-10 h-5 rounded-full relative transition-colors ${clForm.auto_cleanup_enabled ? "bg-green-600" : "bg-zinc-700"}`}
                  onClick={() => setClForm({ ...clForm, auto_cleanup_enabled: !clForm.auto_cleanup_enabled })}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${clForm.auto_cleanup_enabled ? "translate-x-5" : "translate-x-0.5"}`}></div>
                </div>
                <span className="text-xs font-mono text-zinc-400">Авто</span>
              </label>
            </div>
          </div>
          <div className="flex items-start gap-2 text-[10px] font-mono text-zinc-600">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-yellow-600" />
            <span>Удаляются только фото загруженные через систему и НЕ привязанные к товарам/событиям. Перед удалением выполняется проверка по всей базе.</span>
          </div>
        </div>

        <button onClick={handleClSave} disabled={clSaving}
          className="h-11 px-8 bg-white text-black text-sm font-display font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-50"
          data-testid="cl-save-btn">
          {clSaving ? "..." : "СОХРАНИТЬ CLOUDINARY"}
        </button>

        {/* Stats */}
        {clStats && (
          <div className="mt-6 border border-zinc-800 p-4">
            <h4 className="text-[11px] font-mono text-zinc-500 uppercase tracking-wider mb-3">СТАТИСТИКА ХРАНИЛИЩА</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <div className="text-2xl font-mono text-white">{clStats.total_tracked}</div>
                <div className="text-[10px] font-mono text-zinc-600">ВСЕГО ЗАГРУЖЕНО</div>
              </div>
              <div>
                <div className="text-2xl font-mono text-green-400">{clStats.in_use}</div>
                <div className="text-[10px] font-mono text-zinc-600">ИСПОЛЬЗУЕТСЯ</div>
              </div>
              <div>
                <div className="text-2xl font-mono text-yellow-400">{clStats.unused}</div>
                <div className="text-[10px] font-mono text-zinc-600">НЕ ИСПОЛЬЗУЕТСЯ</div>
              </div>
              <div>
                <div className="text-2xl font-mono text-red-400">{clStats.stale_unused}</div>
                <div className="text-[10px] font-mono text-zinc-600">К УДАЛЕНИЮ ({clStats.cleanup_minutes} мин)</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleCleanup} disabled={clCleaning || clStats.stale_unused === 0}
                className="h-9 px-4 bg-red-900/50 border border-red-800 text-red-300 text-xs font-mono uppercase hover:bg-red-900 transition-colors disabled:opacity-30 flex items-center gap-2"
                data-testid="cl-cleanup-btn">
                <Trash2 className="w-3.5 h-3.5" />
                {clCleaning ? "УДАЛЕНИЕ..." : `ОЧИСТИТЬ (${clStats.stale_unused})`}
              </button>
              <button onClick={handleSync} disabled={clSyncing}
                className="h-9 px-4 bg-zinc-800 border border-zinc-700 text-zinc-300 text-xs font-mono uppercase hover:bg-zinc-700 transition-colors disabled:opacity-30 flex items-center gap-2"
                data-testid="cl-sync-btn">
                <RefreshCw className={`w-3.5 h-3.5 ${clSyncing ? "animate-spin" : ""}`} />
                {clSyncing ? "..." : "СИНХРОНИЗАЦИЯ"}
              </button>
            </div>
            {clStats.auto_cleanup_enabled && (
              <p className="text-[10px] font-mono text-green-600 mt-2">
                ✓ Автоочистка активна — проверка каждые 5 мин
              </p>
            )}
          </div>
        )}
      </div>

      {/* Database Backup Section */}
      <div className="border-t border-zinc-800 pt-8">
        <div className="flex items-center gap-2 mb-4">
          <Download className="w-5 h-5 text-emerald-400" />
          <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider">БЭКАП БАЗЫ ДАННЫХ</h3>
        </div>
        <div className="max-w-xl space-y-3">
          <p className="text-xs text-zinc-400 leading-relaxed">
            Скачайте полный снимок базы данных в формате JSON. Файл содержит все коллекции
            (товары, заказы, категории, промо, настройки и т.д.) и может быть использован
            для восстановления или миграции на другого провайдера MongoDB.
          </p>
          <div className="flex items-start gap-2 p-3 bg-zinc-900/60 border border-zinc-800">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[11px] font-mono text-zinc-400 leading-relaxed">
              Файл может содержать конфиденциальные данные (заказы, контакты клиентов,
              настройки). Храните его в защищённом месте.
            </p>
          </div>
          <button
            onClick={handleDownloadBackup}
            disabled={downloadingBackup}
            className="h-11 px-6 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-display font-bold uppercase tracking-wider transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="download-backup-btn"
          >
            <Download className={`w-4 h-4 ${downloadingBackup ? "animate-pulse" : ""}`} />
            {downloadingBackup ? "СКАЧИВАНИЕ..." : "СКАЧАТЬ БЭКАП БАЗЫ"}
          </button>

          <div className="border-t border-zinc-800/60 pt-4 mt-4 space-y-2">
            <p className="text-xs text-zinc-400 leading-relaxed">
              <b className="text-zinc-200">Архив фотографий</b> — отдельный ZIP, содержащий все фото
              товаров и событий с <code className="text-[10px] text-zinc-500">manifest.json</code>
              (соответствие имени файла товару). На случай повторной блокировки Cloudinary
              у вас будет полная копия медиа-файлов на диске.
            </p>
            <button
              onClick={handleDownloadPhotos}
              disabled={downloadingPhotos}
              className="h-11 px-6 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-display font-bold uppercase tracking-wider transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="download-photos-btn"
            >
              <Image className={`w-4 h-4 ${downloadingPhotos ? "animate-pulse" : ""}`} />
              {downloadingPhotos ? "СКАЧИВАНИЕ..." : "СКАЧАТЬ АРХИВ ФОТО"}
            </button>
            {photosProgress && downloadingPhotos && (
              <div className="text-[11px] font-mono text-zinc-400 space-y-1" data-testid="photos-progress">
                {photosProgress.status === "starting" && <p>Запуск...</p>}
                {photosProgress.total > 0 && (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 bg-zinc-800 rounded-sm overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 transition-all duration-300"
                          style={{ width: `${Math.round((photosProgress.downloaded / photosProgress.total) * 100)}%` }}
                        />
                      </div>
                      <span className="text-zinc-300 shrink-0">
                        {photosProgress.downloaded} / {photosProgress.total}
                      </span>
                    </div>
                    <p>
                      ✓ {photosProgress.succeeded}
                      {photosProgress.failed > 0 && <span className="text-red-400 ml-2">✗ {photosProgress.failed}</span>}
                      {photosProgress.status === "ready" && <span className="text-emerald-400 ml-2">• готов, скачиваем...</span>}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Telegram Auto-Backup Section */}
      <div className="border-t border-zinc-800 pt-8">
        <div className="flex items-center gap-2 mb-4">
          <Bot className="w-5 h-5 text-sky-400" />
          <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider">АВТО-БЭКАП В TELEGRAM</h3>
        </div>
        <div className="max-w-xl space-y-4">
          <p className="text-xs text-zinc-400 leading-relaxed">
            Бэкап базы (сжатый в ZIP) автоматически отправляется в admin-чат бота с заданной периодичностью.
            По этому файлу можно полностью восстановить базу на любом провайдере MongoDB.
          </p>

          {!tgBackup.admin_chat_id && (
            <div className="flex items-start gap-2 p-3 bg-amber-950/30 border border-amber-900/50">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[11px] font-mono text-amber-300 leading-relaxed">
                Admin-чат не настроен. Откройте бота в Telegram и отправьте команду /start, чтобы привязать ваш чат.
              </p>
            </div>
          )}

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={tgBackup.telegram_backup_enabled}
              onChange={(e) => setTgBackup({ ...tgBackup, telegram_backup_enabled: e.target.checked })}
              className="w-4 h-4 accent-sky-500"
              data-testid="tg-backup-enabled-toggle"
            />
            <span className="text-sm text-zinc-200">Включить автоматическую отправку бэкапа</span>
          </label>

          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Период отправки (часы)</label>
            <input
              type="number" min="1" max="168"
              value={tgBackup.telegram_backup_interval_hours}
              onChange={(e) => setTgBackup({ ...tgBackup, telegram_backup_interval_hours: parseInt(e.target.value) || 24 })}
              className="w-28 bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono"
              data-testid="tg-backup-interval"
            />
            <p className="text-[11px] text-zinc-600 mt-1">По умолчанию 24 часа. Проверка каждые 5 минут.</p>
          </div>

          {tgBackup.telegram_backup_last_sent && (
            <p className="text-[11px] font-mono text-zinc-500" data-testid="tg-backup-last-sent">
              Последняя отправка: {new Date(tgBackup.telegram_backup_last_sent).toLocaleString("ru-RU")}
              {tgBackup.telegram_backup_last_status && tgBackup.telegram_backup_last_status !== "success" && (
                <span className="text-red-400 ml-2">• {tgBackup.telegram_backup_last_status}</span>
              )}
              {tgBackup.telegram_backup_last_status === "success" && (
                <span className="text-emerald-400 ml-2">• ok</span>
              )}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSaveTgBackup}
              disabled={savingTgBackup}
              className="h-11 px-6 bg-white text-black text-sm font-display font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-50"
              data-testid="tg-backup-save-btn"
            >
              {savingTgBackup ? "..." : "СОХРАНИТЬ"}
            </button>
            <button
              onClick={handleSendTgBackupNow}
              disabled={sendingTgBackup || !tgBackup.admin_chat_id}
              className="h-11 px-6 bg-sky-600 hover:bg-sky-500 text-white text-sm font-display font-bold uppercase tracking-wider transition-colors disabled:opacity-40 flex items-center gap-2"
              data-testid="tg-backup-send-now-btn"
            >
              <Bot className={`w-4 h-4 ${sendingTgBackup ? "animate-pulse" : ""}`} />
              {sendingTgBackup ? "ОТПРАВКА..." : "ОТПРАВИТЬ В TELEGRAM СЕЙЧАС"}
            </button>
          </div>
        </div>
      </div>

      {/* Restore Section */}
      <div className="border-t border-zinc-800 pt-8">
        <div className="flex items-center gap-2 mb-4">
          <Upload className="w-5 h-5 text-red-400" />
          <h3 className="text-sm font-mono text-zinc-500 uppercase tracking-wider">ВОССТАНОВЛЕНИЕ БАЗЫ ИЗ БЭКАПА</h3>
        </div>
        <div className="max-w-xl space-y-4">
          <div className="flex items-start gap-2 p-3 bg-red-950/30 border border-red-900/50">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <div className="text-[11px] font-mono text-red-300 leading-relaxed space-y-1">
              <p><b>ОПАСНО:</b> восстановление ПОЛНОСТЬЮ заменит все коллекции, указанные в файле бэкапа.</p>
              <p>Перед восстановлением обязательно скачайте свежий бэкап текущей базы.</p>
              <p>Для подтверждения потребуется действующий код 2FA.</p>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Файл бэкапа (.json или .zip)</label>
            <input
              type="file"
              accept=".json,.zip,application/json,application/zip"
              onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
              className="block w-full text-xs text-zinc-300 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-zinc-800 file:text-white file:text-xs file:font-mono file:uppercase hover:file:bg-zinc-700"
              data-testid="restore-file-input"
            />
            {restoreFile && (
              <p className="text-[11px] font-mono text-zinc-500 mt-1">
                {restoreFile.name} — {Math.round(restoreFile.size / 1024)} KB
              </p>
            )}
          </div>

          <div>
            <label className="text-[11px] font-mono text-zinc-500 uppercase block mb-1">Код 2FA (из приложения-аутентификатора)</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={restoreCode}
              onChange={(e) => setRestoreCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              className="w-40 bg-zinc-900 border border-zinc-800 h-11 px-3 text-sm text-white focus:outline-none focus:border-zinc-600 font-mono tracking-[0.3em] text-center"
              data-testid="restore-2fa-input"
            />
          </div>

          <button
            onClick={handleRestore}
            disabled={restoring || !restoreFile || restoreCode.length !== 6}
            className="h-11 px-6 bg-red-700 hover:bg-red-600 text-white text-sm font-display font-bold uppercase tracking-wider transition-colors disabled:opacity-40 flex items-center gap-2"
            data-testid="restore-submit-btn"
          >
            <Upload className={`w-4 h-4 ${restoring ? "animate-pulse" : ""}`} />
            {restoring ? "ВОССТАНОВЛЕНИЕ..." : "ВОССТАНОВИТЬ БАЗУ"}
          </button>

          {restoreResult && (
            <div className="p-3 bg-emerald-950/30 border border-emerald-900/50 space-y-1" data-testid="restore-result">
              <p className="text-xs font-mono text-emerald-300">
                ✓ Восстановлено: {restoreResult.total_docs} документов в {Object.keys(restoreResult.restored_collections || {}).length} коллекциях
              </p>
              <p className="text-[10px] font-mono text-zinc-500">
                Источник: {restoreResult.source_meta?.db_name} • {restoreResult.source_meta?.created_at}
              </p>
              {restoreResult.errors && restoreResult.errors.length > 0 && (
                <p className="text-[11px] font-mono text-red-400">Ошибки: {restoreResult.errors.join("; ")}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      adminVerify().then(() => setAuthed(true)).catch(() => {
        localStorage.removeItem("admin_token");
      }).finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("admin_token");
    setAuthed(false);
  };

  if (checking) {
    return <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="flex items-end gap-[3px]">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="w-[3px] bg-white origin-bottom" style={{ animation: `szLoader 1s ease-in-out ${i * 0.12}s infinite`, height: "24px" }} />
        ))}
      </div>
    </div>;
  }

  if (!authed) return <LoginForm onLogin={() => setAuthed(true)} />;

  return <AdminDashboard onLogout={logout} />;
}

function AdminDashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState("products");
  const [openChatCustomerId, setOpenChatCustomerId] = useState(null);

  const openCustomerChat = (customerId) => {
    setOpenChatCustomerId(customerId);
    setActiveTab("messages");
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 w-full bg-black/90 backdrop-blur-sm border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-3">
            <Link to="/" className="p-2 hover:bg-zinc-900 transition-colors" data-testid="admin-back-link">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <span className="font-display text-base tracking-wider">ADMIN PANEL</span>
          </div>
          <button onClick={onLogout} className="p-2 hover:bg-zinc-900 transition-colors" data-testid="admin-logout-btn">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>
      <div className="max-w-5xl mx-auto p-4 md:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full bg-zinc-900 border border-zinc-800 rounded-none h-auto p-0 grid grid-cols-5 gap-0">
            {[
              { val: "products", icon: Package, label: "Товары" },
              { val: "events", icon: Newspaper, label: "Лента" },
              { val: "categories", icon: FolderOpen, label: "Категории" },
              { val: "orders", icon: ShoppingCart, label: "Заказы" },
              { val: "customers", icon: User, label: "Клиенты" },
              { val: "messages", icon: MessageCircle, label: "Сообщения" },
              { val: "promos", icon: Tag, label: "Промо" },
              { val: "settings", icon: Settings, label: "Настройки" },
              { val: "bot", icon: Bot, label: "Бот" },
              { val: "security", icon: Shield, label: "Безопасность" },
            ].map((t) => (
              <TabsTrigger key={t.val} value={t.val}
                className="rounded-none h-11 px-2 text-[10px] sm:text-[11px] font-mono uppercase data-[state=active]:bg-zinc-800 data-[state=active]:text-white text-zinc-500 gap-1.5 flex items-center justify-center min-w-0"
                data-testid={`admin-tab-${t.val}`}>
                <t.icon className="w-3.5 h-3.5 shrink-0" />
                <span className="hidden sm:inline truncate">{t.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="mt-4 md:mt-6">
            <TabsContent value="products"><ProductsTab /></TabsContent>
            <TabsContent value="events"><EventsTab /></TabsContent>
            <TabsContent value="categories"><CategoriesTab /></TabsContent>
            <TabsContent value="orders"><OrdersTab /></TabsContent>
            <TabsContent value="customers"><CustomersTab onOpenChat={openCustomerChat} /></TabsContent>
            <TabsContent value="messages"><MessagesTab initialCustomerId={openChatCustomerId} onConsumed={() => setOpenChatCustomerId(null)} /></TabsContent>
            <TabsContent value="promos"><PromosTab /></TabsContent>
            <TabsContent value="settings"><StoreSettingsTab /></TabsContent>
            <TabsContent value="bot"><BotTab /></TabsContent>
            <TabsContent value="security"><SecurityTab /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}